// Generado por la división del monolito.
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  MONTHS,
  fmt,
  fmtK,
  Ic,
  aggregateHistory,
  billingFromDeals,
  billingFromDealsByMonth,
  getUserTeamForMonth,
  getTeamUserIds,
  getUserRoleForMonth,
  getUserLevelForMonth,
} from './common.jsx';
import { KpiCard, KpiSkeleton, TooltipIcon } from './ui/index.jsx';
import { useFirstVisitTour } from './tour.jsx';
// Lote-136: estado de cierre mensual (hook para la acción pendiente del Inicio)
import { useMonthClosure } from './closures.jsx';
// Lote-110: lógica de scope compartida entre frontend y backend
// (el módulo es CJS, esbuild lo bundlea automáticamente).
import { dealTeamId as _dealTeamId } from '../lib/scope.js';

// lote-182: fila de "acción pendiente" (izquierda) y de "info relevante"
// (derecha) de los paneles operativos del Inicio.
function ActionRow({ done, title, detail, onClick, action }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span style={{ fontSize: 16 }}>{done ? '✅' : '⏳'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
        <div style={{ fontSize: 11, color: done ? 'var(--gr-d)' : 'var(--tx-mu)' }}>{detail}</div>
      </div>
      {action || (onClick && <span style={{ color: 'var(--tx-mu)', fontSize: 16 }}>›</span>)}
    </div>
  );
}

function InfoRow({ label, value, sub, delta, onClick }) {
  const d = typeof delta === 'number' ? delta : null;
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      title={clickable ? 'Ver desglose' : undefined}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 10,
        cursor: clickable ? 'pointer' : 'default',
        borderRadius: 6,
        margin: '-2px -6px',
        padding: '2px 6px',
        transition: 'background .12s',
      }}
      onMouseEnter={e => {
        if (clickable) e.currentTarget.style.background = 'var(--bg)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: 'var(--tx-mu)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {label}
        {clickable && <span style={{ color: 'var(--tx3)', fontSize: 13 }}>›</span>}
      </div>
      <div style={{ textAlign: 'right', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          {value}
          {d != null && d !== 0 && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                fontWeight: 700,
                color: d > 0 ? 'var(--gr-d)' : 'var(--er)',
              }}
            >
              {d > 0 ? '▲' : '▼'} {Math.abs(d)}
            </span>
          )}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--tx-mu)',
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

// lote-187: modal de desglose de "Info relevante" (clic en una fila).
function InfoDetailModal({
  kind,
  onClose,
  pmLabel,
  altas,
  bajas,
  current,
  prevByComercial,
  prevBrutaTotal,
  prevNetaTotal,
  tramoChanges,
  equipoChanges,
}) {
  const title =
    kind === 'comerciales'
      ? 'Comerciales del equipo'
      : kind === 'bruta'
        ? `Comisión bruta por comercial · ${pmLabel}`
        : kind === 'neta'
          ? `Comisión neta por comercial · ${pmLabel}`
          : kind === 'tramo'
            ? `Cambios de tramo · vs ${pmLabel}`
            : `Cambios de equipo · vs ${pmLabel}`;
  const NameList = ({ items, empty }) =>
    items.length === 0 ? (
      <div style={{ color: 'var(--tx-mu)', fontSize: 13 }}>{empty}</div>
    ) : (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((n, i) => (
          <span
            key={i}
            className="pill"
            style={{ background: 'var(--bg)', fontSize: 12, padding: '3px 8px' }}
          >
            {n}
          </span>
        ))}
      </div>
    );
  const MoneyRows = ({ field }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <tbody>
        {prevByComercial.map((r, i) => (
          <tr key={i} style={{ borderTop: i ? '1px solid var(--bds)' : 'none' }}>
            <td style={{ padding: '6px 4px' }}>{r.name}</td>
            <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600 }}>
              {fmt(r[field])}
            </td>
          </tr>
        ))}
        <tr style={{ borderTop: '2px solid var(--bds)' }}>
          <td style={{ padding: '6px 4px', fontWeight: 700 }}>Total</td>
          <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 800 }}>
            {fmt(field === 'bruta' ? prevBrutaTotal : prevNetaTotal)}
          </td>
        </tr>
      </tbody>
    </table>
  );
  const ChangeRows = ({ items }) =>
    items.length === 0 ? (
      <div style={{ color: 'var(--tx-mu)', fontSize: 13 }}>Sin cambios vs el mes anterior.</div>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {items.map((c, i) => (
            <tr key={i} style={{ borderTop: i ? '1px solid var(--bds)' : 'none' }}>
              <td style={{ padding: '6px 4px', fontWeight: 600 }}>{c.name}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right', color: 'var(--tx-mu)' }}>
                {c.from} <span style={{ color: 'var(--pu)' }}>→</span> {c.to}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  // lote-195: drawer lateral (mismo patrón que ComercialDetailDrawer) en vez de
  // modal centrado — más elegante y con el design system de la app.
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,20,20,.35)',
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(460px,96vw)',
          height: '100vh',
          background: 'var(--wh)',
          overflow: 'auto',
          boxShadow: '-8px 0 24px rgba(0,0,0,.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="mh">
          <h3>{title}</h3>
          <button className="btn btn-gh btn-ic" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="mb" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {kind === 'comerciales' && (
            <>
              <div>
                <div
                  style={{ fontSize: 11, fontWeight: 700, color: 'var(--gr-d)', marginBottom: 6 }}
                >
                  Altas vs {pmLabel} ({altas.length})
                </div>
                <NameList items={altas.map(u => u.name)} empty="Ninguna alta." />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--er)', marginBottom: 6 }}>
                  Bajas / salidas vs {pmLabel} ({bajas.length})
                </div>
                <NameList items={bajas.map(u => u.name)} empty="Ninguna baja." />
              </div>
              <div>
                <div
                  style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-mu)', marginBottom: 6 }}
                >
                  Equipo actual ({current.length})
                </div>
                <NameList items={current.map(u => u.name)} empty="Sin comerciales." />
              </div>
            </>
          )}
          {kind === 'bruta' && <MoneyRows field="bruta" />}
          {kind === 'neta' && <MoneyRows field="neta" />}
          {kind === 'tramo' && <ChangeRows items={tramoChanges} />}
          {kind === 'equipo' && <ChangeRows items={equipoChanges} />}
        </div>
      </div>
    </div>
  );
}

export function ManagerHomePage({
  allUsers,
  teams,
  lockedTeamId = null,
  goTo,
  setProfileUser,
  isManager = true,
  activeUser = null,
}) {
  // Si el viewer es un TL (manager_comercial) mostramos arriba del Inicio una
  // tarjeta con su progreso de comisión TL del Q en curso. Si no tiene plan
  // TL asignado, mostramos un empty state minimalista.
  const isTLViewer = activeUser?.role === 'manager_comercial';
  // Tour interactivo: dispara la versión TL o la manager según rol.
  const tour = useFirstVisitTour({
    tourId: isTLViewer ? 'inicio_tl_v1' : 'inicio_manager_v1',
    activeUser,
  });
  useEffect(() => {
    const fn = () => {
      tour.launch();
      window.dispatchEvent(new CustomEvent('tour:relaunch-ack'));
    };
    window.addEventListener('tour:relaunch', fn);
    return () => window.removeEventListener('tour:relaunch', fn);
  }, [tour]);
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  // 'month' = un mes concreto; 'year' = acumulado anual completo;
  // 'custom' = rango mensual personalizado (puede cruzar años).
  const [viewMode, setViewMode] = useState('month');
  // Rango personalizado: por defecto los últimos 3 meses contando el actual.
  const _defaultFrom = (() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  })();
  const [customFromY, setCustomFromY] = useState(_defaultFrom.y);
  const [customFromM, setCustomFromM] = useState(_defaultFrom.m);
  const [customToY, setCustomToY] = useState(now.getFullYear());
  const [customToM, setCustomToM] = useState(now.getMonth() + 1);
  const [selTeamId, setSelTeamId] = useState(lockedTeamId || '');
  const [drill, setDrill] = useState(null);
  useEffect(() => {
    if (lockedTeamId) setSelTeamId(lockedTeamId);
  }, [lockedTeamId]);
  const isYear = viewMode === 'year';
  const isCustom = viewMode === 'custom';
  // En modo año/custom no filtramos por mes concreto en los helpers que sí aceptan
  // year+month, así que pasamos null y aplicamos el filtro fino arriba.
  const effMonth = isYear || isCustom ? null : selMonth;
  // Año "principal" para los helpers que solo aceptan year (rankings, charts):
  // en custom usamos el toY como referencia para gráficas que pintan "este año".
  const effYearRef = isCustom ? customToY : selYear;
  // Comparador (y,m) en el rango activo. Cubre los 3 modos.
  const inActiveRange = (y, m) => {
    if (isCustom) {
      const k = y * 100 + m;
      return k >= customFromY * 100 + customFromM && k <= customToY * 100 + customToM;
    }
    if (isYear) return y === selYear;
    return y === selYear && m === selMonth;
  };
  // Itera meses del rango activo. Útil para sumar billingFromDeals por mes.
  const iterRangeMonths = fn => {
    if (isCustom) {
      for (let y = customFromY; y <= customToY; y++) {
        const mStart = y === customFromY ? customFromM : 1;
        const mEnd = y === customToY ? customToM : 12;
        for (let m = mStart; m <= mEnd; m++) fn(y, m);
      }
    } else if (isYear) {
      for (let m = 1; m <= 12; m++) fn(selYear, m);
    } else {
      fn(selYear, selMonth);
    }
  };
  // Label legible del periodo, para títulos.
  const rangeLabel = isCustom
    ? `${MONTHS[customFromM - 1]} ${customFromY} – ${MONTHS[customToM - 1]} ${customToY}`
    : isYear
      ? `año ${selYear}`
      : `${MONTHS[selMonth - 1]} ${selYear}`;

  const [history, setHistory] = useState([]);
  const [syncs, setSyncs] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [payments, setPayments] = useState([]);
  const [incomplete, setIncomplete] = useState([]);
  const [balances, setBalances] = useState({});
  const [syncStatus, setSyncStatus] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  // Lote-133: subteams + team-objectives consolidados (distintos de los
  // objetivos individuales de comerciales en user.monthlyObjectives).
  const [subteams, setSubteams] = useState([]);
  const [teamObjectives, setTeamObjectives] = useState([]);
  // Lote-187: tramos (para mostrar nombres en el desglose de cambios de tramo).
  const [levels, setLevels] = useState([]);
  // Lote-182: acuses mensuales "tramos y equipo siguen igual" (confirmar tramos).
  const [confirmations, setConfirmations] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const reloadConfirmations = useCallback(() => {
    fetch('/api/team-confirmations')
      .then(r => r.json())
      .then(d => setConfirmations(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetch('/api/subteams')
      .then(r => r.json())
      .then(setSubteams)
      .catch(() => {});
    fetch('/api/team-objectives')
      .then(r => r.json())
      .then(setTeamObjectives)
      .catch(() => {});
    fetch('/api/levels')
      .then(r => r.json())
      .then(d => setLevels(Array.isArray(d) ? d : []))
      .catch(() => {});
    reloadConfirmations();
  }, [reloadConfirmations]);
  // Lote-187: detalle abierto al pulsar una fila de "Info relevante".
  const [infoDetail, setInfoDetail] = useState(null);
  // Loading inicial — true hasta que los fetches iniciales resuelvan.
  // Sirve para mostrar skeletons en KPIs en vez de "0" / "—" provisorios.
  const [initialLoading, setInitialLoading] = useState(true);
  // Comercial seleccionado al clicar una cajita → abre drawer con desglose
  // (comisiones + bajas + ventas) sin sacar al manager de Inicio.
  const [detailComercial, setDetailComercial] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/history')
        .then(r => r.json())
        .catch(() => []),
      fetch('/api/hubspot/syncs?withDeals=1')
        .then(r => r.json())
        .catch(() => []),
      fetch('/api/refunds')
        .then(r => r.json())
        .catch(() => []),
      fetch('/api/payments')
        .then(r => r.json())
        .catch(() => []),
      fetch('/api/payments/incomplete')
        .then(r => r.json())
        .catch(() => []),
      fetch('/api/balances')
        .then(r => r.json())
        .catch(() => ({})),
      fetch('/api/sync-status')
        .then(r => r.json())
        .catch(() => ({})),
    ]).then(([h, s, r, p, i, b, st]) => {
      // Defensa: si un endpoint devuelve 401/error, `r.json()` resuelve con el
      // body de error (un objeto), no un array — y `.catch(()=>[])` NO lo
      // atrapa (el fetch tuvo éxito). Sin coercionar, `refunds.filter(...)`
      // peta y tumba la sección. Coercionamos a la forma esperada.
      setHistory(Array.isArray(h) ? h : []);
      setSyncs(Array.isArray(s) ? s : []);
      setRefunds(Array.isArray(r) ? r : []);
      setPayments(Array.isArray(p) ? p : []);
      setIncomplete(Array.isArray(i) ? i : []);
      setBalances(b && typeof b === 'object' && !Array.isArray(b) ? b : {});
      setSyncStatus(st && typeof st === 'object' && !Array.isArray(st) ? st : {});
      setInitialLoading(false);
    });
  }, []);

  const teamFilter = lockedTeamId || selTeamId;

  // Lote-123 (fix lote-125): facturación canónica via /api/sales (defensiva).
  // CRÍTICO: este bloque DEBE ir después de la declaración de `teamFilter`,
  // sino caemos en TDZ y lanzamos ReferenceError → React tree colapsa →
  // pantalla blanca para todo el manager. (Bug que tumbó la plataforma
  // tras lote-123.)
  //
  // Patrón: el cálculo "local" desde dealsMonth sigue intacto. Cuando
  // salesByOwner llega del backend, SOLO sobreescribe billing/sales en los
  // cards. Si la fetch falla o devuelve vacío, los cards muestran el dato
  // local (no rompemos render). Garantiza que Inicio cuadre con Ventas/
  // HubSpot cuando el endpoint responde, y que NUNCA muestre pantalla
  // blanca si algo falla.
  //
  // salesByOwner empieza como null. cardsByOwner detecta null vs {} para
  // saber si ya respondió la fetch (incluso con 0 sales).
  const [salesByOwner, setSalesByOwner] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams();
    if (isCustom) {
      const fromIso = new Date(customFromY, customFromM - 1, 1).toISOString().slice(0, 10);
      const toIso = new Date(customToY, customToM, 0).toISOString().slice(0, 10);
      qs.set('from', fromIso);
      qs.set('to', toIso);
    } else {
      if (selYear) qs.set('year', String(selYear));
      if (!isYear) qs.set('month', String(selMonth));
    }
    if (teamFilter) qs.set('teamId', teamFilter);
    fetch('/api/sales?' + qs.toString())
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        if (!data || !Array.isArray(data.sales)) {
          setSalesByOwner(null); // no hay datos canónicos → cards usan local
          return;
        }
        const out = {};
        for (const s of data.sales) {
          const oid = s?.ownerId;
          if (!oid) continue;
          if (!out[oid]) out[oid] = { billing: 0, count: 0 };
          out[oid].billing += parseFloat(s.amount) || 0;
          out[oid].count += 1;
        }
        setSalesByOwner(out);
      })
      .catch(() => {
        if (!cancelled) setSalesByOwner(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selYear,
    selMonth,
    isYear,
    isCustom,
    teamFilter,
    customFromY,
    customFromM,
    customToY,
    customToM,
  ]);
  const ownerById = useMemo(
    () => Object.fromEntries(allUsers.map(u => [u.hubspotOwnerId, u])),
    [allUsers]
  );
  const ownerTeam = oid => ownerById[oid]?.teamId;
  // Lote-110: dealTeam ahora viene de lib/scope.js — misma función que usa
  // el backend para /api/sales, /api/cobros, /api/stats. Lote-107 introdujo
  // el helper local; este lote lo unifica con la fuente backend.
  const dealTeam = d => _dealTeamId(d, d ? ownerById[d.ownerId] : null);

  // Comerciales activos del scope: SOLO role=comercial (para el KPI "Comerciales activos" y la
  // tabla "Comerciales del mes"). Los sumatorios globales (facturación, comisión, pago neto)
  // SÍ incluyen a managers/operaciones que también facturen — sólo el conteo se restringe.
  //
  // Lote-128: usamos el equipo y rol DEL MES seleccionado (monthlyTeams /
  // monthlyRoles), no el predeterminado actual. Si en mayo un comercial estaba
  // en FP y luego pasó a Opos, al ver mayo del equipo FP debe aparecer.
  // En modo año/custom usamos el teamId actual como fallback (no hay un mes
  // único de referencia).
  const comerciales = allUsers.filter(u => {
    const role = effMonth != null ? getUserRoleForMonth(u, selYear, effMonth) : u.role;
    if (role !== 'comercial') return false;
    if ((u.status || 'activo') !== 'activo') return false;
    if (teamFilter) {
      const team = effMonth != null ? getUserTeamForMonth(u, selYear, effMonth) : u.teamId;
      if (team !== teamFilter) return false;
    }
    return true;
  });
  // wasComercialAt: respeta monthlyRoles. Un user que pasó a manager en H2
  // se sigue contando como comercial en H1 (cuando hubo overrides). Si no
  // hay override, cae al user.role actual.
  const userByIdAll = new Map(allUsers.map(u => [u.id, u]));
  const wasComercialAt = (uid, y, m) => {
    const u = userByIdAll.get(uid);
    if (!u) return false;
    if (getUserRoleForMonth(u, y, m) !== 'comercial') return false;
    if (teamFilter && getUserTeamForMonth(u, y, m) !== teamFilter) return false;
    if ((u.status || 'activo') !== 'activo') return false;
    return true;
  };
  // Cuando hay filtro de equipo, el scope incluye TODOS los users del equipo (cualquier rol)
  // para que el manager con teamId que factura siga contando. En modo año/custom
  // pasamos null como mes para usar el teamId actual del user (no per-mes).
  const teamUserIdSet = teamFilter
    ? getTeamUserIds(
        allUsers,
        teamFilter,
        isYear || isCustom ? null : selYear,
        isYear || isCustom ? null : selMonth
      )
    : null;

  // Historial del mes/año/rango. En custom no filtramos por year/month en el
  // helper y post-filtramos con inActiveRange.
  const monthEntries = isCustom
    ? aggregateHistory(history, { teamUserIds: teamUserIdSet }).filter(h =>
        inActiveRange(h.year, h.month)
      )
    : aggregateHistory(history, { year: selYear, month: effMonth, teamUserIds: teamUserIdSet });
  // Subset sólo-comerciales (con rol del mes) para la tabla "Comerciales del mes".
  const comercialMonthEntries = monthEntries.filter(h => wasComercialAt(h.userId, h.year, h.month));

  // Facturación BRUTA del periodo desde syncs.deals. En custom sumamos cada mes
  // del rango (billingFromDeals solo acepta year+month puntuales).
  const billingInfo = (() => {
    if (isCustom) {
      let total = 0,
        count = 0;
      iterRangeMonths((y, m) => {
        const r = billingFromDeals(syncs, allUsers, {
          year: y,
          month: m,
          teamId: teamFilter || null,
        });
        total += r.total;
        count += r.count;
      });
      return { total, count };
    }
    return billingFromDeals(syncs, allUsers, {
      year: selYear,
      month: effMonth,
      teamId: teamFilter || null,
    });
  })();
  const billing = billingInfo.total;
  const commission = monthEntries.reduce((s, h) => s + (h.result?.totalCommission || 0), 0);
  const adjustments = monthEntries.reduce((s, h) => s + (h.adjustmentsTotal || 0), 0); // ≤0
  const finalPayout = monthEntries.reduce(
    (s, h) => s + (h.finalPayout != null ? h.finalPayout : h.result?.totalCommission || 0),
    0
  );
  const activeComerciales = comercialMonthEntries.filter(
    h => (h.result?.totalCommission || 0) > 0
  ).length;

  // Bajas/impagos: limitamos al periodo seleccionado (mes / año / rango).
  // closedate viene como ISO (YYYY-MM-DD). Comparación lexicográfica funciona en YMD.
  // BUG ANTIGUO: en modo Rango (isCustom) caíamos al `else` con selYear/selMonth,
  // dejando el rango sin efecto en cajitas/records/cards. Ahora se respeta.
  const periodStartIso = isCustom
    ? new Date(customFromY, customFromM - 1, 1).toISOString().slice(0, 10)
    : isYear
      ? new Date(selYear, 0, 1).toISOString().slice(0, 10)
      : new Date(selYear, selMonth - 1, 1).toISOString().slice(0, 10);
  const periodEndIso = isCustom
    ? new Date(customToY, customToM, 0).toISOString().slice(0, 10)
    : isYear
      ? new Date(selYear, 12, 0).toISOString().slice(0, 10)
      : new Date(selYear, selMonth, 0).toISOString().slice(0, 10);
  const inMonth = iso =>
    iso && iso.slice(0, 10) >= periodStartIso && iso.slice(0, 10) <= periodEndIso;
  // dealById hoisted (lote-107): se usa también desde refundsScoped para
  // resolver vertical del deal vinculado a la baja.
  // Lote-120: mismo problema de orden que dealsScoped — sort ASC para
  // que last-write-wins se quede con el sync más reciente del deal
  // (verticalTeamId/amount actualizados).
  const dealById = useMemo(() => {
    const m = {};
    const sorted = [...syncs].sort((a, b) =>
      (a.importedAt || '').localeCompare(b.importedAt || '')
    );
    sorted.forEach(s =>
      (s.deals || []).forEach(d => {
        m[d.id] = d;
      })
    );
    return m;
  }, [syncs]);
  // Lote-107: refunds van por vertical del deal asociado (no del owner).
  // Si el refund no tiene deal vinculado en syncs (rare), cae a owner team.
  const refundsScoped = refunds.filter(r => {
    if (!teamFilter) return true;
    const d = dealById[r.dealId];
    return (d ? dealTeam(d) : ownerTeam(r.ownerId)) === teamFilter;
  });
  const refundsMonth = refundsScoped.filter(r => inMonth(r.closedate));

  // Impagos: contar payments cuyo deal asociado tiene closedate en el periodo.
  const paymentsScoped = payments.filter(p => {
    const d = dealById[p.dealId];
    if (!d) return false;
    // Lote-107: vertical del deal manda sobre el owner.
    if (teamFilter && dealTeam(d) !== teamFilter) return false;
    return true;
  });
  const paymentsMonth = paymentsScoped.filter(p => {
    const d = dealById[p.dealId];
    return d?.closedate && inMonth(d.closedate);
  });
  // Impagos firmes separados por número de cuota (solo 2ª y 3ª penalizan en el cálculo)
  const paymentsMonth2 = paymentsMonth.filter(p => p.installmentNumber === 2);
  const paymentsMonth3 = paymentsMonth.filter(p => p.installmentNumber === 3);

  // Pendientes de revisión (huérfanos) — scoped
  const incompleteScoped = incomplete.filter(c => {
    if (!teamFilter) return true;
    const owners = c.ownerIds || [];
    return owners.some(oid => ownerTeam(oid) === teamFilter);
  });

  // Matrículas del mes (deals con closedate en el mes).
  // Lote-107: el equipo de la venta es deal.verticalTeamId (HubSpot
  // "Vertical by Ratings"). Antes filtrábamos por equipo del owner, lo
  // que provocaba que el record de Opos no cuadrara con HubSpot ni con
  // la pestaña Ventas (esa ya migró en lote-102). Fallback a owner team
  // cuando el deal no trae vertical (pre-lote-100).
  //
  // Lote-117: deduplicamos por dealId. El mismo deal puede aparecer en
  // varios sync entries (ej. import manual + auto-sync del mismo mes, o
  // cuando el closedate cambia y un barrido posterior lo re-graba). Sin
  // dedup, dealsMonth/dealsScoped lo contaban dos veces y cardsByOwner
  // sumaba `amount` también dos veces. Caso real: Juan Gordon mayo
  // aparecía con 7316€ en Inicio vs 4737€ en HubSpot/Ventas.
  //
  // Lote-120 (hotfix): el endpoint /api/hubspot/syncs devuelve los syncs
  // en orden DESC por importedAt (más reciente primero). Iterar en ese
  // orden con `byId.set` dejaba al final el sync MÁS VIEJO — y nos
  // quedábamos con la versión vieja del deal (amount/closedate antiguos),
  // que podía hasta excluir el deal del mes si su closedate había cambiado.
  // Solución: sortar ASC en el frontend antes del dedup → la última
  // iteración por dealId es la del sync más reciente. Defensivo respecto
  // al orden del endpoint y robusto si en el futuro alguien lo cambia.
  const dealsScoped = useMemo(() => {
    const byId = new Map();
    const sorted = [...syncs].sort((a, b) =>
      (a.importedAt || '').localeCompare(b.importedAt || '')
    );
    sorted.forEach(s =>
      (s.deals || []).forEach(d => {
        if (teamFilter && dealTeam(d) !== teamFilter) return;
        if (!d.id) return;
        byId.set(String(d.id), d);
      })
    );
    return Array.from(byId.values());
  }, [syncs, teamFilter, allUsers]);
  const dealsMonth = dealsScoped.filter(d => inMonth(d.closedate));

  // ── YoY: mismo periodo año anterior ──
  // Para Mes: mes M del año Y-1. Para Año: año entero Y-1. Para Custom:
  // ventana desplazada -1 año. No mostramos si el periodo prior no ha
  // ocurrido aún (mes futuro del año pasado no aplica) o si la app no tiene
  // historial suficiente.
  const priorPeriod = (() => {
    let startIso,
      endIso,
      periodMul = 1;
    if (isCustom) {
      const fy = customFromY - 1,
        ty = customToY - 1;
      startIso = new Date(fy, customFromM - 1, 1).toISOString().slice(0, 10);
      endIso = new Date(ty, customToM, 0).toISOString().slice(0, 10);
    } else if (isYear) {
      startIso = new Date(selYear - 1, 0, 1).toISOString().slice(0, 10);
      endIso = new Date(selYear - 1, 12, 0).toISOString().slice(0, 10);
    } else {
      startIso = new Date(selYear - 1, selMonth - 1, 1).toISOString().slice(0, 10);
      endIso = new Date(selYear - 1, selMonth, 0).toISOString().slice(0, 10);
    }
    return { startIso, endIso, periodMul };
  })();
  const inPriorPeriod = iso =>
    iso && iso.slice(0, 10) >= priorPeriod.startIso && iso.slice(0, 10) <= priorPeriod.endIso;
  const priorDealsMonth = dealsScoped.filter(d => inPriorPeriod(d.closedate));
  const priorBilling = priorDealsMonth.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const priorHistory = aggregateHistory(history, { teamUserIds: teamUserIdSet }).filter(h => {
    const iso = new Date(h.year, h.month - 1, 15).toISOString().slice(0, 10);
    return inPriorPeriod(iso);
  });
  const priorCommission = priorHistory.reduce((s, h) => s + (h.result?.totalCommission || 0), 0);
  const priorRefundsMonth = refundsScoped.filter(r => inPriorPeriod(r.closedate));
  // Helpers para Pct YoY: solo mostramos si el periodo prior tuvo datos
  // suficientes (>0) — comparar con 0 da Infinity y no es informativo.
  const yoy = (current, prior) => (prior > 0 ? ((current - prior) / prior) * 100 : null);
  const yoyBilling = yoy(billing, priorBilling);
  const yoyCommission = yoy(commission, priorCommission);
  // Label para tooltip del badge: "vs mismo periodo 2025: 12.345 €"
  const yoyLabelFor = (priorVal, fmtFn) =>
    priorVal > 0 ? `vs mismo periodo año anterior: ${fmtFn(priorVal)}` : '';

  // Saldo deudor total del equipo (negativo = deuda)
  const balanceUserIds = teamFilter
    ? Object.keys(balances).filter(uid => teamUserIdSet.has(uid))
    : Object.keys(balances);
  const totalDebt = balanceUserIds.reduce((s, uid) => s + Math.min(0, balances[uid] || 0), 0);

  const years = [
    ...new Set([selYear, now.getFullYear(), now.getFullYear() - 1, ...history.map(h => h.year)]),
  ].sort((a, b) => b - a);

  const runFullSync = async () => {
    if (!confirm('Sincronizar deals + bajas + impagos desde HubSpot ahora?')) return;
    setSyncing(true);
    setSyncMsg('Sincronizando…');
    try {
      await fetch('/api/hubspot/auto-sync', { method: 'POST' });
      setSyncMsg('✓ Sincronización completada');
      // Recargar todo
      const [h, s, r, p, i, b, st] = await Promise.all([
        fetch('/api/history').then(r => r.json()),
        fetch('/api/hubspot/syncs?withDeals=1').then(r => r.json()),
        fetch('/api/refunds').then(r => r.json()),
        fetch('/api/payments').then(r => r.json()),
        fetch('/api/payments/incomplete').then(r => r.json()),
        fetch('/api/balances').then(r => r.json()),
        fetch('/api/sync-status').then(r => r.json()),
      ]);
      // Misma defensa que la carga inicial: coercionar por si un endpoint
      // devolvió un body de error en vez del array/objeto esperado.
      setHistory(Array.isArray(h) ? h : []);
      setSyncs(Array.isArray(s) ? s : []);
      setRefunds(Array.isArray(r) ? r : []);
      setPayments(Array.isArray(p) ? p : []);
      setIncomplete(Array.isArray(i) ? i : []);
      setBalances(b && typeof b === 'object' && !Array.isArray(b) ? b : {});
      setSyncStatus(st && typeof st === 'object' && !Array.isArray(st) ? st : {});
    } catch (e) {
      setSyncMsg('Error: ' + e.message);
    }
    setSyncing(false);
  };

  const lastDeals = syncStatus.deals?.at
    ? new Date(syncStatus.deals.at).toLocaleString('es-ES')
    : '—';

  // Comerciales únicos que vendieron este mes (owners con al menos 1 deal).
  const owners = useMemo(() => {
    const s = new Set();
    dealsMonth.forEach(d => {
      if (d.ownerId) s.add(d.ownerId);
    });
    return s;
  }, [dealsMonth]);
  const billingPerCom = owners.size > 0 ? billing / owners.size : 0;
  const commPct = billing > 0 ? (commission / billing) * 100 : 0;

  // Agregado por comercial del mes — la "card FIFA". Identidad: ownerId si lo hay,
  // si no userId. Cruzamos deals (facturación + nº ventas), history (comisión bruta,
  // pago neto, ajustes), refunds y payments (bajas e impagos) para tener todo en
  // un solo objeto por persona.
  // Días transcurridos / totales del periodo seleccionado. Sirve para la
  // proyección de facturación (regla de tres). Si el periodo ya pasó, no
  // proyectamos (multiplicador = 1). Si es futuro, multiplicador = 0.
  const periodDays = useMemo(() => {
    const today = now;
    // Rango custom: días totales = (toDay - fromDay + 1). Si el rango cubre
    // hoy, elapsed es lo que va transcurrido; si ya pasó, total; si futuro, 0.
    if (isCustom) {
      const start = new Date(customFromY, customFromM - 1, 1);
      const end = new Date(customToY, customToM, 0); // último día del mes Hasta
      const total = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
      if (today < start) return { elapsed: 0, total };
      if (today > end) return { elapsed: total, total };
      const elapsed = Math.max(1, Math.round((today - start) / (1000 * 60 * 60 * 24)) + 1);
      return { elapsed: Math.min(elapsed, total), total };
    }
    if (isYear) {
      const start = new Date(selYear, 0, 1);
      const end = new Date(selYear + 1, 0, 1);
      const total = Math.round((end - start) / (1000 * 60 * 60 * 24));
      if (selYear < today.getFullYear()) return { elapsed: total, total }; // año pasado
      if (selYear > today.getFullYear()) return { elapsed: 0, total };
      const elapsed = Math.max(1, Math.round((today - start) / (1000 * 60 * 60 * 24)) + 1);
      return { elapsed: Math.min(elapsed, total), total };
    }
    const start = new Date(selYear, selMonth - 1, 1);
    const end = new Date(selYear, selMonth, 0); // último día del mes
    const total = end.getDate();
    const sameYearMonth = selYear === today.getFullYear() && selMonth === today.getMonth() + 1;
    if (sameYearMonth) return { elapsed: today.getDate(), total };
    // ¿mes pasado o futuro?
    return end < today ? { elapsed: total, total } : { elapsed: 0, total };
  }, [isYear, isCustom, selYear, selMonth, customFromY, customFromM, customToY, customToM, now]);
  const projectionMul = periodDays.elapsed > 0 ? periodDays.total / periodDays.elapsed : 0;
  // isCurrentPeriod cubre los 3 modos. En custom = hoy cae dentro del rango.
  const isCurrentPeriod = isCustom
    ? (() => {
        const todayKey = now.getFullYear() * 100 + (now.getMonth() + 1);
        return (
          todayKey >= customFromY * 100 + customFromM && todayKey <= customToY * 100 + customToM
        );
      })()
    : isYear
      ? selYear === now.getFullYear()
      : selYear === now.getFullYear() && selMonth === now.getMonth() + 1;

  // Objetivo por user en el periodo seleccionado.
  // - Modo mes: lee monthlyObjectives[year_month] del user.
  // - Modo año: suma todos los meses del año del user.
  const objectiveOf = u => {
    const m = u?.monthlyObjectives || {};
    if (isYear) {
      let s = 0;
      for (const [k, v] of Object.entries(m)) {
        if (k.startsWith(selYear + '_') && v != null) s += parseFloat(v) || 0;
      }
      return s;
    }
    const v = m[selYear + '_' + selMonth];
    return v != null ? parseFloat(v) || 0 : null;
  };

  const cardsByOwner = useMemo(() => {
    const byKey = {};
    const ensure = (key, owner) => {
      if (!byKey[key])
        byKey[key] = {
          key,
          ownerId: owner?.hubspotOwnerId,
          userId: owner?.id || null,
          name: owner?.name || '—',
          teamId: owner?.teamId || null,
          billing: 0,
          sales: 0,
          gross: 0,
          net: 0,
          refunds: 0,
          unpaid: 0,
          objective: null, // null = sin objetivo configurado
        };
      return byKey[key];
    };
    // Facturación + nº ventas (desde syncs.deals del mes y scope)
    dealsMonth.forEach(d => {
      const owner = ownerById[d.ownerId];
      const key = owner?.id || d.ownerId || '_un_' + d.ownerName;
      const r = ensure(key, owner);
      if (!r.name || r.name === '—') r.name = owner?.name || d.ownerName || '—';
      if (!r.ownerId) r.ownerId = d.ownerId;
      if (r.objective == null && owner) r.objective = objectiveOf(owner);
      r.billing += parseFloat(d.amount) || 0;
      r.sales += 1;
    });
    // Comisión bruta + pago neto (desde history del mes/scope, deduplicado)
    monthEntries.forEach(h => {
      const owner = h.userId ? allUsers.find(u => u.id === h.userId) : ownerById[h.ownerId];
      const key = owner?.id || h.ownerId || h.userId || '_un_' + h.userName;
      const r = ensure(key, owner);
      r.gross += h.result?.totalCommission || 0;
      r.net += h.finalPayout != null ? h.finalPayout : h.result?.totalCommission || 0;
    });
    // Bajas (refunds del mes scope)
    refundsMonth.forEach(rf => {
      const owner = ownerById[rf.ownerId];
      const key = owner?.id || rf.ownerId || '_un_' + rf.ownerName;
      const r = ensure(key, owner);
      r.refunds += 1;
    });
    // Impagos firmes del mes (2ª + 3ª)
    paymentsMonth.forEach(p => {
      const d = dealById[p.dealId];
      const owner = ownerById[d?.ownerId];
      const key = owner?.id || d?.ownerId || '_un_' + (d?.ownerName || '');
      const r = ensure(key, owner);
      r.unpaid += 1;
    });
    // Lote-123: si /api/sales respondió, sobreescribimos billing/sales con
    // el valor canónico (cuadra con Ventas y HubSpot). Si no respondió aún
    // o falló, dejamos el cálculo local de dealsMonth. Defensivo: nunca
    // rompe el render.
    if (salesByOwner && typeof salesByOwner === 'object') {
      Object.values(byKey).forEach(r => {
        const canonical = r.ownerId ? salesByOwner[r.ownerId] : null;
        if (canonical) {
          r.billing = canonical.billing;
          r.sales = canonical.count;
        }
      });
    }
    return Object.values(byKey).filter(r => r.billing > 0 || r.sales > 0); // solo con ventas
  }, [
    dealsMonth,
    monthEntries,
    refundsMonth,
    paymentsMonth,
    ownerById,
    allUsers,
    dealById,
    salesByOwner,
  ]);
  const cards = [...cardsByOwner].sort((a, b) => b.billing - a.billing);

  // ── lote-181 (1.1): desglose por SUBEQUIPO de la vertical en scope. ──
  // Reutiliza la agregación por comercial (cardsByOwner) + la membresía de
  // subequipos (subteam.userIds) + los objetivos consolidados. El subequipo
  // del TL que mira se resalta. Se omiten los subequipos sin comerciales
  // (p.ej. el residual B2B, que no tiene userIds).
  const usersById = useMemo(() => new Map(allUsers.map(u => [u.id, u])), [allUsers]);
  const mySubteamId =
    (activeUser &&
      (
        subteams.find(s => s.managerId === activeUser.id) ||
        subteams.find(s => (s.userIds || []).includes(activeUser.id))
      )?.id) ||
    null;
  const subteamRows = (() => {
    const cardByUser = new Map();
    for (const c of cardsByOwner) if (c.userId) cardByUser.set(c.userId, c);
    // lote-188: meses del rango activo (mes / año / custom).
    const activeMonths = (() => {
      if (isYear) return Array.from({ length: 12 }, (_, i) => ({ y: selYear, m: i + 1 }));
      if (isCustom) {
        const out = [];
        let y = customFromY;
        let mm = customFromM;
        while (y < customToY || (y === customToY && mm <= customToM)) {
          out.push({ y, m: mm });
          mm++;
          if (mm > 12) {
            mm = 1;
            y++;
          }
        }
        return out;
      }
      return [{ y: selYear, m: selMonth }];
    })();
    // lote-188: objetivo del subequipo = Σ objetivos individuales + extra manual,
    // sumado sobre los meses del rango activo (consistente con la pág. Objetivos).
    const objOfSub = s => {
      let total = 0;
      for (const { y, m } of activeMonths) {
        for (const uid of s.userIds || []) {
          const u = usersById.get(uid);
          if (u) total += parseFloat((u.monthlyObjectives || {})[`${y}_${m}`]) || 0;
        }
        const rec = (teamObjectives || []).find(
          o =>
            o.teamId === s.teamId && (o.subteamId || null) === s.id && o.year === y && o.month === m
        );
        if (rec) total += parseFloat(rec.extra) || 0;
      }
      return total;
    };
    return (subteams || [])
      .filter(s => (s.userIds || []).length > 0 && (!teamFilter || s.teamId === teamFilter))
      .map(s => {
        let billing = 0;
        let commission = 0;
        for (const uid of s.userIds || []) {
          const c = cardByUser.get(uid);
          if (c) {
            billing += c.billing || 0;
            commission += c.gross || 0;
          }
        }
        const count = (s.userIds || []).filter(uid => {
          const u = usersById.get(uid);
          return u && u.role === 'comercial' && (u.status || 'activo') === 'activo';
        }).length;
        const objetivo = objOfSub(s);
        return {
          id: s.id,
          name: s.name,
          billing,
          commission,
          count,
          objetivo,
          pct: objetivo > 0 ? Math.round((billing / objetivo) * 100) : null,
        };
      })
      .sort((a, b) => b.billing - a.billing);
  })();

  // ── lote-182 (1.2): paneles "Acciones pendientes" + "Info relevante". ──
  // Anclados en el mes seleccionado (solo tienen sentido en vista mes). La
  // info relevante compara contra el mes inmediatamente anterior.
  const showOpsPanels = !isYear && !isCustom;
  const _prevD = new Date(selYear, selMonth - 2, 1);
  const pmY = _prevD.getFullYear();
  const pmM = _prevD.getMonth() + 1;
  const monthClosure = useMonthClosure(selYear, selMonth);

  // Acciones pendientes —
  // 1) objetivos sin poner de los comerciales del scope este mes
  const objKeyRef = selYear + '_' + selMonth;
  const comercialesSinObjetivo = comerciales.filter(
    u => !(parseFloat((u.monthlyObjectives || {})[objKeyRef]) > 0)
  );
  const objetivosOk = comerciales.length > 0 && comercialesSinObjetivo.length === 0;
  // 2) cierre del mes cerrado para el scope (global o el team del scope)
  const mesCerrado =
    monthClosure.active.some(c => c.scope === 'global') ||
    (!!teamFilter && monthClosure.active.some(c => c.scope === 'team' && c.scopeId === teamFilter));
  // 3) confirmación de tramos/equipo de este mes (solo si hay equipo en scope)
  const confirmacionMes =
    teamFilter &&
    confirmations.find(c => c.teamId === teamFilter && c.year === selYear && c.month === selMonth);
  const confirmTramos = async () => {
    if (!teamFilter) return;
    setConfirming(true);
    try {
      await fetch('/api/team-confirmations', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId: teamFilter,
          year: selYear,
          month: selMonth,
          _actor: activeUser?.id ? { id: activeUser.id } : undefined,
        }),
      });
      reloadConfirmations();
    } catch {
      /* noop */
    } finally {
      setConfirming(false);
    }
  };

  // Info relevante — comparativa vs mes anterior
  const prevComerciales = allUsers.filter(u => wasComercialAt(u.id, pmY, pmM));
  const deltaComerciales = comerciales.length - prevComerciales.length;
  // lote-187: altas/bajas de comerciales del scope vs mes anterior.
  const curComIds = new Set(comerciales.map(u => u.id));
  const prevComIds = new Set(prevComerciales.map(u => u.id));
  const altasComerciales = comerciales.filter(u => !prevComIds.has(u.id));
  const bajasComerciales = prevComerciales.filter(u => !curComIds.has(u.id));
  const prevComEntries = aggregateHistory(history, {
    year: pmY,
    month: pmM,
    teamUserIds: teamUserIdSet,
  }).filter(h => wasComercialAt(h.userId, pmY, pmM));
  const prevBrutaTotal = prevComEntries.reduce((s, h) => s + (h.result?.totalCommission || 0), 0);
  const prevNetaTotal = prevComEntries.reduce(
    (s, h) => s + (h.finalPayout != null ? h.finalPayout : h.result?.totalCommission || 0),
    0
  );
  const prevActivos = prevComEntries.filter(h => (h.result?.totalCommission || 0) > 0).length;
  const prevBrutaAvg = prevActivos ? prevBrutaTotal / prevActivos : 0;
  const prevNetaAvg = prevActivos ? prevNetaTotal / prevActivos : 0;
  // lote-187: desglose por comercial del mes anterior (bruta + neta).
  const prevByComercial = prevComEntries
    .map(h => ({
      name: userByIdAll.get(h.userId)?.name || h.userName || '—',
      bruta: h.result?.totalCommission || 0,
      neta: h.finalPayout != null ? h.finalPayout : h.result?.totalCommission || 0,
    }))
    .filter(x => x.bruta > 0 || x.neta > 0)
    .sort((a, b) => b.bruta - a.bruta);
  // cambios de tramo / equipo vs mes anterior (unión de miembros cur + prev).
  // lote-187: guardamos de→a (nombres) para el desglose al pulsar.
  const _levelName = id => levels.find(l => l.id === id)?.name || (id ? '—' : 'Sin tramo');
  const _teamName = id => (teams || []).find(t => t.id === id)?.name || (id ? '—' : 'Sin equipo');
  const changeUserIds = new Set([...comerciales.map(u => u.id), ...prevComerciales.map(u => u.id)]);
  const tramoChanges = [];
  const equipoChanges = [];
  for (const uid of changeUserIds) {
    const u = userByIdAll.get(uid);
    if (!u) continue;
    const cl = getUserLevelForMonth(u, selYear, selMonth);
    const pl = getUserLevelForMonth(u, pmY, pmM);
    if (cl !== pl) tramoChanges.push({ name: u.name, from: _levelName(pl), to: _levelName(cl) });
    const ct = getUserTeamForMonth(u, selYear, selMonth);
    const pt = getUserTeamForMonth(u, pmY, pmM);
    if (ct !== pt) equipoChanges.push({ name: u.name, from: _teamName(pt), to: _teamName(ct) });
  }

  // Ranking de comisiones (solo role=comercial, comisión > 0).
  // En modo año agregamos por user (suma de todos los meses); en modo mes hay 1 entry
  // por user gracias al dedup de aggregateHistory, así que el resultado es el mismo.
  const ranking = (() => {
    const byUser = {};
    for (const h of comercialMonthEntries) {
      const uid = h.userId || h.userName || '_';
      const c = h.result?.totalCommission || 0;
      const n = h.finalPayout != null ? h.finalPayout : c;
      if (!byUser[uid])
        byUser[uid] = {
          userId: h.userId,
          userName: h.userName,
          user: allUsers.find(u => u.id === h.userId),
          comm: 0,
          net: 0,
          refId: h.id,
          billing: 0,
          sales: 0,
        };
      byUser[uid].comm += c;
      byUser[uid].net += n;
    }
    // Cruzamos con la facturación del periodo desde cards (deals→billing).
    // Buscamos por userId y, en su defecto, por nombre del owner.
    for (const r of Object.values(byUser)) {
      const card = cardsByOwner.find(
        c => (r.userId && c.userId === r.userId) || (c.name && r.userName && c.name === r.userName)
      );
      if (card) {
        r.billing = card.billing || 0;
        r.sales = card.sales || 0;
      }
    }
    return Object.values(byUser)
      .filter(r => r.comm > 0)
      .sort((a, b) => b.comm - a.comm);
  })();

  // KPI tipo PCC: valor siempre en foreground; el color solo se usa para alertas (rojo).
  // Si se pasa onClick, la card es interactiva (hover + cursor pointer) y abre detalle.
  // Pestaña activa dentro de Inicio. Los filtros del topbar se mantienen
  // arriba y aplican a las 3 vistas.
  const [activeTab, setActiveTab] = useState('resumen');
  const TabBtn = ({ t, label }) => (
    <button
      onClick={() => setActiveTab(t)}
      style={{
        padding: '10px 20px',
        fontSize: 13,
        fontWeight: activeTab === t ? 700 : 500,
        color: activeTab === t ? 'var(--pu)' : 'var(--tx3)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        borderBottom: activeTab === t ? '2px solid var(--pu)' : '2px solid transparent',
        marginBottom: -2,
        transition: 'all .15s',
      }}
    >
      {label}
    </button>
  );

  // KpiCard, KpiSkeleton y TooltipIcon viven en src/ui/index.jsx — se reusan
  // entre páginas. Si necesitas cambiarles el look, ahí.

  const QuickAction = ({ icon, label, onClick }) => (
    <button
      onClick={onClick}
      className="card"
      style={{
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: '#fff',
        border: '1px solid var(--bd)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background .15s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
    >
      <div
        style={{
          flex: '0 0 32px',
          height: 32,
          borderRadius: 8,
          background: 'var(--pu-bg)',
          color: 'var(--pu)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>{label}</div>
    </button>
  );

  // ── Insights + Records (debajo de las cajitas de Métricas) ──
  // Persistencia: localStorage per-rol manager. Si en el futuro queremos
  // compartir entre managers, migramos a un endpoint.
  const periodKey = isCustom
    ? `${customFromY}-${String(customFromM).padStart(2, '0')}_to_${customToY}-${String(customToM).padStart(2, '0')}`
    : isYear
      ? String(selYear)
      : `${selYear}-${String(selMonth).padStart(2, '0')}`;
  const scopeKey = teamFilter || 'all';
  const mkId = (kind, ref) => `insight:${kind}:${ref}:${periodKey}:${scopeKey}`;

  // Estado persistido en backend (/api/insights). Antes localStorage —
  // ahora se comparte entre dispositivos y entre managers.
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [acceptedItems, setAcceptedItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/insights')
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setDismissedIds(new Set(data.dismissed || []));
        setAcceptedItems(data.accepted || []);
      })
      .catch(() => {
        /* fallback silencioso: empieza vacío */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const dismissInsight = id => {
    // Optimista: actualizamos UI primero, backend después. Si el POST falla,
    // se vuelve a sincronizar en la próxima carga.
    setDismissedIds(prev => {
      const n = new Set(prev);
      n.add(id);
      return n;
    });
    fetch('/api/insights/dismiss', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    }).catch(() => {});
  };
  const acceptInsight = ins => {
    const payload = {
      id: ins.id,
      icon: ins.icon,
      title: ins.title,
      message: ins.message,
      kind: ins.kind,
      period: periodKey,
      scope: scopeKey,
    };
    setAcceptedItems(prev =>
      prev.some(x => x.id === ins.id)
        ? prev
        : [...prev, { ...payload, acceptedAt: new Date().toISOString() }]
    );
    setDismissedIds(prev => {
      const n = new Set(prev);
      n.add(ins.id);
      return n;
    });
    fetch('/api/insights/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };
  const removeAccepted = id => {
    setAcceptedItems(prev => prev.filter(x => x.id !== id));
    fetch('/api/insights/done', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    }).catch(() => {});
  };

  // Computa insights del periodo activo. Cada uno tiene un id estable basado
  // en (kind, ref, periodKey, scopeKey) para que persista entre recargas.
  const rawInsights = useMemo(() => {
    const out = [];
    // 1) Comerciales con tasa de bajas por encima de media + 50% (≥2 bajas, ≥3 ventas).
    const cardsWithSales = cards.filter(c => c.sales >= 3);
    if (cardsWithSales.length >= 3) {
      const avg =
        cardsWithSales.reduce((s, c) => s + c.refunds / c.sales, 0) / cardsWithSales.length;
      for (const c of cardsWithSales) {
        const pct = c.refunds / c.sales;
        if (pct > avg * 1.5 && c.refunds >= 2) {
          out.push({
            id: mkId('high-bajas', c.key),
            kind: 'bajas',
            icon: '🔻',
            tone: 'er',
            title: `${c.name}: % bajas alto`,
            message: `${(pct * 100).toFixed(1)}% bajas vs ${(avg * 100).toFixed(1)}% media · ${c.refunds} bajas de ${c.sales} ventas`,
            userId: c.userId,
          });
        }
      }
    }
    // 2) Comerciales con impagos por encima de la media (≥2 impagos, ≥3 ventas).
    if (cardsWithSales.length >= 3) {
      const avg =
        cardsWithSales.reduce((s, c) => s + c.unpaid / c.sales, 0) / cardsWithSales.length;
      for (const c of cardsWithSales) {
        const pct = c.unpaid / c.sales;
        if (pct > avg * 1.5 && c.unpaid >= 2) {
          out.push({
            id: mkId('high-impagos', c.key),
            kind: 'impagos',
            icon: '⚠️',
            tone: 'or',
            title: `${c.name}: % impagos alto`,
            message: `${(pct * 100).toFixed(1)}% impagos vs ${(avg * 100).toFixed(1)}% media · ${c.unpaid} impagos de ${c.sales} ventas`,
            userId: c.userId,
          });
        }
      }
    }
    // 3) Comerciales con ritmo proyectado < 70% del objetivo (solo periodo en curso).
    if (isCurrentPeriod && projectionMul > 1) {
      for (const c of cards) {
        if (c.objective && c.objective > 0) {
          const projected = c.billing * projectionMul;
          const ratio = projected / c.objective;
          if (ratio < 0.7) {
            out.push({
              id: mkId('low-pace', c.key),
              kind: 'pace',
              icon: '🐢',
              tone: 'or',
              title: `${c.name}: ritmo por debajo del objetivo`,
              message: `Proyección ${fmt(projected)} vs objetivo ${fmt(c.objective)} (${(ratio * 100).toFixed(0)}%)`,
              userId: c.userId,
            });
          }
        }
      }
    }
    // 4) Pasarela con % bajas elevado (≥5 ventas en la pasarela).
    const gwTotal = {},
      gwBajas = {};
    for (const d of dealsMonth) {
      const g = d.gateway || 'desconocido';
      gwTotal[g] = (gwTotal[g] || 0) + 1;
    }
    for (const r of refundsMonth) {
      const d = dealById[r.dealId];
      const g = d?.gateway || 'desconocido';
      gwBajas[g] = (gwBajas[g] || 0) + 1;
    }
    const gws = Object.keys(gwTotal).filter(g => gwTotal[g] >= 5 && g !== 'desconocido');
    if (gws.length >= 2) {
      const avg = gws.reduce((s, g) => s + (gwBajas[g] || 0) / gwTotal[g], 0) / gws.length;
      for (const g of gws) {
        const pct = (gwBajas[g] || 0) / gwTotal[g];
        if (pct > avg * 1.5 && (gwBajas[g] || 0) >= 2) {
          out.push({
            id: mkId('gateway-bajas', g),
            kind: 'gateway',
            icon: '💳',
            tone: 'or',
            title: `Pasarela ${g}: % bajas elevado`,
            message: `${(pct * 100).toFixed(1)}% bajas vs ${(avg * 100).toFixed(1)}% media · ${gwBajas[g] || 0} bajas de ${gwTotal[g]} ventas`,
          });
        }
      }
    }
    // 5) Formación con tasa de baja alta (≥5 ventas en la formación).
    const fTotal = {},
      fBajas = {};
    for (const d of dealsMonth) {
      const f = d.formacion || 'Sin clasificar';
      fTotal[f] = (fTotal[f] || 0) + 1;
    }
    for (const r of refundsMonth) {
      const d = dealById[r.dealId];
      const f = r.formacion || d?.formacion || 'Sin clasificar';
      fBajas[f] = (fBajas[f] || 0) + 1;
    }
    const fs = Object.keys(fTotal).filter(f => fTotal[f] >= 5 && f !== 'Sin clasificar');
    if (fs.length >= 2) {
      const avg = fs.reduce((s, f) => s + (fBajas[f] || 0) / fTotal[f], 0) / fs.length;
      for (const f of fs) {
        const pct = (fBajas[f] || 0) / fTotal[f];
        if (pct > avg * 1.5 && (fBajas[f] || 0) >= 2) {
          out.push({
            id: mkId('formacion-bajas', f),
            kind: 'formacion',
            icon: '📚',
            tone: 'or',
            title: `Formación ${f}: % bajas alto`,
            message: `${(pct * 100).toFixed(1)}% bajas vs ${(avg * 100).toFixed(1)}% media · ${fBajas[f] || 0} bajas de ${fTotal[f]} ventas`,
          });
        }
      }
    }
    return out;
  }, [
    cards,
    dealsMonth,
    refundsMonth,
    dealById,
    isCurrentPeriod,
    projectionMul,
    periodKey,
    scopeKey,
  ]);

  const visibleInsights = rawInsights.filter(i => !dismissedIds.has(i.id));

  // ── Records del periodo activo ──
  const isoWeekKey = date => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
  };
  // Formatters de fechas para records — siempre en castellano.
  //   fmtDay   "2026-05-22"     → "22 Mayo 2026"
  //   fmtMonth "2026-03"        → "Marzo 2026"
  //   fmtWeek  "2026-W17"       → "22-28 Abril 2026" (mismo mes)
  //                              → "30 Sep – 6 Oct 2026" (cruza mes)
  const fmtDay = iso => {
    if (!iso || iso.length < 10) return iso || '';
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return iso;
    return `${d} ${MONTHS[m - 1]} ${y}`;
  };
  const fmtMonth = yyyyMm => {
    if (!yyyyMm) return '';
    const [y, m] = yyyyMm.split('-').map(Number);
    if (!y || !m) return yyyyMm;
    return `${MONTHS[m - 1]} ${y}`;
  };
  const fmtWeek = yyyyWww => {
    if (!yyyyWww) return '';
    const match = /^(\d{4})-W(\d{2})$/.exec(yyyyWww);
    if (!match) return yyyyWww;
    const y = parseInt(match[1]),
      w = parseInt(match[2]);
    // Lunes de la semana 1 = el lunes ≤ 4 de enero. ISO 8601.
    const jan4 = new Date(Date.UTC(y, 0, 4));
    const dow = jan4.getUTCDay() || 7; // domingo=7
    const start = new Date(jan4);
    start.setUTCDate(jan4.getUTCDate() - dow + 1 + (w - 1) * 7);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    const sD = start.getUTCDate(),
      sM = start.getUTCMonth(),
      sY = start.getUTCFullYear();
    const eD = end.getUTCDate(),
      eM = end.getUTCMonth(),
      eY = end.getUTCFullYear();
    if (sY === eY && sM === eM) return `${sD}-${eD} ${MONTHS[sM]} ${sY}`;
    if (sY === eY) return `${sD} ${MONTHS[sM].slice(0, 3)} – ${eD} ${MONTHS[eM].slice(0, 3)} ${sY}`;
    return `${sD} ${MONTHS[sM].slice(0, 3)} ${sY} – ${eD} ${MONTHS[eM].slice(0, 3)} ${eY}`;
  };
  // Excluidos de los records individuales (no de los agregados). Antes
  // hardcoded por nombre (Álvaro Gordon / Lino Monteagudo). Ahora se lee
  // del flag `excludedFromRecords` en el user, gestionable desde el perfil.
  // Identificamos al user por id o por hubspotOwnerId — lo que tengamos.
  const excludedSet = useMemo(() => {
    const ids = new Set();
    const owners = new Set();
    for (const u of allUsers || []) {
      if (u.excludedFromRecords) {
        if (u.id) ids.add(u.id);
        if (u.hubspotOwnerId) owners.add(String(u.hubspotOwnerId));
      }
    }
    return { ids, owners };
  }, [allUsers]);
  const isExcludedCard = c => {
    if (!c) return false;
    if (c.userId && excludedSet.ids.has(c.userId)) return true;
    if (c.ownerId && excludedSet.owners.has(String(c.ownerId))) return true;
    return false;
  };
  const isExcludedOwnerId = oid => oid != null && excludedSet.owners.has(String(oid));

  const records = useMemo(() => {
    const out = [];
    // Mejor día (suma de amount por closedate.YYYY-MM-DD)
    const byDay = {};
    for (const d of dealsMonth) {
      const k = (d.closedate || '').slice(0, 10);
      if (!k) continue;
      byDay[k] = (byDay[k] || 0) + (parseFloat(d.amount) || 0);
    }
    const dayRows = Object.entries(byDay)
      .map(([day, v]) => ({ key: day, label: fmtDay(day), value: v, valueFmt: fmt(v) }))
      .sort((a, b) => b.value - a.value);
    if (dayRows[0])
      out.push({
        kind: 'best-day',
        label: 'Mejor día',
        value: fmt(dayRows[0].value),
        sub: dayRows[0].label,
        icon: '🌅',
        rows: dayRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Día' },
          { key: 'valueFmt', label: 'Facturación', right: true },
        ],
      });
    // Mejor semana
    const byWeek = {};
    for (const d of dealsMonth) {
      if (!d.closedate) continue;
      const w = isoWeekKey(new Date(d.closedate));
      byWeek[w] = (byWeek[w] || 0) + (parseFloat(d.amount) || 0);
    }
    const weekRows = Object.entries(byWeek)
      .map(([w, v]) => ({ key: w, label: fmtWeek(w), value: v, valueFmt: fmt(v) }))
      .sort((a, b) => b.value - a.value);
    if (weekRows[0] && weekRows.length > 1)
      out.push({
        kind: 'best-week',
        label: 'Mejor semana',
        value: fmt(weekRows[0].value),
        sub: weekRows[0].label,
        icon: '📈',
        rows: weekRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Semana' },
          { key: 'valueFmt', label: 'Facturación', right: true },
        ],
      });
    // Mejor mes (solo si scope abarca >1 mes)
    const byMonth = {};
    for (const d of dealsMonth) {
      if (!d.closedate) continue;
      byMonth[d.closedate.slice(0, 7)] =
        (byMonth[d.closedate.slice(0, 7)] || 0) + (parseFloat(d.amount) || 0);
    }
    const mRows = Object.entries(byMonth)
      .map(([m, v]) => ({ key: m, label: fmtMonth(m), value: v, valueFmt: fmt(v) }))
      .sort((a, b) => b.value - a.value);
    if (mRows.length > 1)
      out.push({
        kind: 'best-month',
        label: 'Mejor mes',
        value: fmt(mRows[0].value),
        sub: mRows[0].label,
        icon: '📅',
        rows: mRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Mes' },
          { key: 'valueFmt', label: 'Facturación', right: true },
        ],
      });
    // Comercial top (por billing) — excluye los marcados con excludedFromRecords
    const filteredCards = cards.filter(c => !isExcludedCard(c));
    const topCRows = [...filteredCards]
      .sort((a, b) => b.billing - a.billing)
      .map(c => ({
        key: c.key,
        label: c.name,
        value: c.billing,
        valueFmt: fmt(c.billing),
        sales: c.sales,
      }));
    if (topCRows[0])
      out.push({
        kind: 'top-comercial',
        label: 'Comercial top facturación',
        value: fmt(topCRows[0].value),
        sub: topCRows[0].label,
        icon: '🏆',
        rows: topCRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Comercial' },
          { key: 'sales', label: 'Ventas', right: true },
          { key: 'valueFmt', label: 'Facturación', right: true },
        ],
      });
    // Comercial top día — excluye los marcados con excludedFromRecords
    const byOwnerDay = {};
    for (const d of dealsMonth) {
      if (!d.closedate || !d.ownerId) continue;
      if (isExcludedOwnerId(d.ownerId)) continue;
      const k = d.closedate.slice(0, 10) + '|' + d.ownerId;
      byOwnerDay[k] = (byOwnerDay[k] || 0) + (parseFloat(d.amount) || 0);
    }
    const odRows = Object.entries(byOwnerDay)
      .map(([k, v]) => {
        const [day, oid] = k.split('|');
        const u = ownerById[oid];
        const dayLabel = fmtDay(day);
        return {
          key: k,
          label: u?.name || '—',
          sub: dayLabel,
          day: dayLabel,
          value: v,
          valueFmt: fmt(v),
        };
      })
      .sort((a, b) => b.value - a.value);
    if (odRows[0])
      out.push({
        kind: 'best-day-com',
        label: 'Mejor día de un comercial',
        value: fmt(odRows[0].value),
        sub: odRows[0].label + ' · ' + odRows[0].day,
        icon: '⭐',
        rows: odRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Comercial' },
          { key: 'day', label: 'Día' },
          { key: 'valueFmt', label: 'Facturación', right: true },
        ],
      });
    // Formación más vendida (no individual, no excluye)
    const formCount = {};
    for (const d of dealsMonth) {
      const f = d.formacion || 'Sin clasificar';
      formCount[f] = (formCount[f] || 0) + 1;
    }
    const fcRows = Object.entries(formCount)
      .filter(([f]) => f !== 'Sin clasificar')
      .map(([f, n]) => ({ key: f, label: f, value: n, valueFmt: `${n} ventas` }))
      .sort((a, b) => b.value - a.value);
    if (fcRows[0])
      out.push({
        kind: 'top-formacion',
        label: 'Formación más vendida',
        value: `${fcRows[0].value} ventas`,
        sub: fcRows[0].label,
        icon: '📚',
        rows: fcRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Formación' },
          { key: 'valueFmt', label: 'Ventas', right: true },
        ],
      });
    // Formación con más bajas + % sobre matrículas
    const formBajas = {};
    for (const r of refundsMonth) {
      const d = dealById[r.dealId];
      const f = r.formacion || d?.formacion || 'Sin clasificar';
      formBajas[f] = (formBajas[f] || 0) + 1;
    }
    const fbRows = Object.entries(formBajas)
      .filter(([f]) => f !== 'Sin clasificar')
      .map(([f, n]) => {
        const sales = formCount[f] || 0;
        const pct = sales > 0 ? (n / sales) * 100 : 0;
        return {
          key: f,
          label: f,
          value: n,
          valueFmt: `${n} bajas`,
          sales,
          pct,
          pctFmt: sales > 0 ? `${pct.toFixed(1)}%` : '—',
        };
      })
      .sort((a, b) => b.value - a.value);
    if (fbRows[0]) {
      const r0 = fbRows[0];
      out.push({
        kind: 'formacion-bajas',
        label: 'Formación con más bajas',
        value: `${r0.value} bajas`,
        // Sub-línea con el % del record sobre matrículas de esa formación.
        sub: r0.label + (r0.sales > 0 ? ` · ${r0.pctFmt} (${r0.value}/${r0.sales})` : ''),
        icon: '🔻',
        rows: fbRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Formación' },
          { key: 'valueFmt', label: 'Bajas', right: true },
          { key: 'sales', label: 'Matrículas', right: true },
          { key: 'pctFmt', label: '% bajas', right: true },
        ],
      });
    }
    // Formación con más impagos
    const formImp = {};
    for (const p of paymentsMonth) {
      const d = dealById[p.dealId];
      const f = d?.formacion || 'Sin clasificar';
      formImp[f] = (formImp[f] || 0) + 1;
    }
    const fiRows = Object.entries(formImp)
      .filter(([f]) => f !== 'Sin clasificar')
      .map(([f, n]) => ({ key: f, label: f, value: n, valueFmt: `${n} impagos` }))
      .sort((a, b) => b.value - a.value);
    if (fiRows[0])
      out.push({
        kind: 'formacion-impagos',
        label: 'Formación con más impagos',
        value: `${fiRows[0].value} impagos`,
        sub: fiRows[0].label,
        icon: '⚠️',
        rows: fiRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Formación' },
          { key: 'valueFmt', label: 'Impagos', right: true },
        ],
      });
    // Mayor comisión en un mes (entry user/mes con más comisión bruta dentro del scope)
    // Solo si el scope abarca >1 mes (año o rango); en vista de un solo mes no tiene gracia.
    const commRows = comercialMonthEntries
      .filter(h => (h.result?.totalCommission || 0) > 0)
      .filter(h => !isExcludedOwnerId(h.ownerId) && !excludedSet.ids.has(h.userId))
      .map(h => ({
        key: (h.userId || h.userName || '') + '_' + h.year + '_' + h.month,
        label: h.userName || ownerById[h.ownerId]?.name || '—',
        month: `${MONTHS[h.month - 1]} ${h.year}`,
        value: h.result.totalCommission,
        valueFmt: fmt(h.result.totalCommission),
      }))
      .sort((a, b) => b.value - a.value);
    const distinctMonths = new Set(commRows.map(r => r.month)).size;
    if (commRows[0] && distinctMonths > 1) {
      const r0 = commRows[0];
      out.push({
        kind: 'best-comm-month',
        label: 'Mayor comisión en un mes',
        value: r0.valueFmt,
        sub: `${r0.label} · ${r0.month}`,
        icon: '💎',
        rows: commRows.slice(0, 10),
        columns: [
          { key: 'label', label: 'Comercial' },
          { key: 'month', label: 'Mes' },
          { key: 'valueFmt', label: 'Comisión', right: true },
        ],
      });
    }
    return out;
  }, [
    dealsMonth,
    refundsMonth,
    paymentsMonth,
    cards,
    dealById,
    ownerById,
    comercialMonthEntries,
    excludedSet,
  ]);

  // Modal con el top 10 del record clicado.
  const [recordModal, setRecordModal] = useState(null);
  // Lote-121: modal "Diagnosticar baja" — busca un nombre y muestra para
  // cada refund matching si entra en la vista actual y por qué (team
  // mismatch, deal no en syncs, vertical=WorkCamp, etc.).
  const [refundDiagnoseOpen, setRefundDiagnoseOpen] = useState(false);
  const [refundDiagnoseQuery, setRefundDiagnoseQuery] = useState('');
  const [refundDiagnoseResult, setRefundDiagnoseResult] = useState(null);
  const [refundDiagnoseLoading, setRefundDiagnoseLoading] = useState(false);
  const runRefundDiagnose = useCallback(async () => {
    const q = (refundDiagnoseQuery || '').trim();
    if (!q) return;
    setRefundDiagnoseLoading(true);
    try {
      const qs = new URLSearchParams({ name: q });
      if (selYear) qs.set('year', String(selYear));
      if (!isYear && !isCustom) qs.set('month', String(selMonth));
      if (teamFilter) qs.set('teamId', teamFilter);
      const r = await fetch('/api/admin/diagnose-refunds?' + qs.toString(), {
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) {
        setRefundDiagnoseResult({ error: data.error || 'Error ' + r.status });
      } else {
        setRefundDiagnoseResult(data);
      }
    } catch (e) {
      setRefundDiagnoseResult({ error: e.message });
    } finally {
      setRefundDiagnoseLoading(false);
    }
  }, [refundDiagnoseQuery, selYear, selMonth, isYear, isCustom, teamFilter]);

  return (
    <>
      {tour.element}
      {/* lote-183 (1.4): el panel de cierre mensual se retira de aquí. El
          estado del cierre vive ahora como indicador en el ítem "Cierre" del
          menú lateral; la acción de cerrar está en "Acciones pendientes". */}
      <div className="topbar">
        <div>
          <h2>Inicio · {rangeLabel}</h2>
          <p>
            {lockedTeamId ? 'Cuadro de mandos de tu equipo' : 'Cuadro de mandos · vista global'} ·
            Última sync HubSpot: {lastDeals}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Toggle Mes / Año / Rango */}
          <div className="tt" style={{ width: 200 }}>
            <div
              className={`to ${viewMode === 'month' ? 'on' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Mes
            </div>
            <div
              className={`to ${viewMode === 'year' ? 'on' : ''}`}
              onClick={() => setViewMode('year')}
            >
              Año
            </div>
            <div
              className={`to ${viewMode === 'custom' ? 'on' : ''}`}
              onClick={() => setViewMode('custom')}
            >
              Rango
            </div>
          </div>
          {viewMode === 'month' && (
            <>
              <select
                className="fs"
                style={{ width: 120, fontSize: 12 }}
                value={selMonth}
                onChange={e => setSelMonth(parseInt(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="fs"
                style={{ width: 90, fontSize: 12 }}
                value={selYear}
                onChange={e => setSelYear(parseInt(e.target.value))}
              >
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </>
          )}
          {viewMode === 'year' && (
            <select
              className="fs"
              style={{ width: 90, fontSize: 12 }}
              value={selYear}
              onChange={e => setSelYear(parseInt(e.target.value))}
            >
              {years.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}
          {viewMode === 'custom' && (
            <>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-mu)' }}>Desde</span>
              <select
                className="fs"
                style={{ width: 110, fontSize: 12 }}
                value={customFromM}
                onChange={e => setCustomFromM(parseInt(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="fs"
                style={{ width: 85, fontSize: 12 }}
                value={customFromY}
                onChange={e => setCustomFromY(parseInt(e.target.value))}
              >
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-mu)' }}>Hasta</span>
              <select
                className="fs"
                style={{ width: 110, fontSize: 12 }}
                value={customToM}
                onChange={e => setCustomToM(parseInt(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="fs"
                style={{ width: 85, fontSize: 12 }}
                value={customToY}
                onChange={e => setCustomToY(parseInt(e.target.value))}
              >
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </>
          )}
          {!lockedTeamId && (teams || []).length > 0 && (
            <select
              className="fs"
              style={{ width: 140, fontSize: 12 }}
              value={selTeamId}
              onChange={e => setSelTeamId(e.target.value)}
            >
              <option value="">Todos los equipos</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className="page">
        {syncMsg && (
          <div
            className="card"
            style={{ padding: 10, marginBottom: 14, fontSize: 12, color: 'var(--tx-mu)' }}
          >
            {syncMsg}
          </div>
        )}

        {/* TL goals card: arriba del Inicio cuando el viewer es un Team Leader.
          Muestra progreso del Q en curso o empty state si no tiene plan TL. */}
        {isTLViewer && (
          <div data-tour="tl-goals-card">
            <TLGoalsCard activeUser={activeUser} goTo={goTo} />
          </div>
        )}

        {/* Tabs de Inicio. Filtros (mes/año/equipo) viven en el topbar y aplican a las 3 vistas. */}
        <div
          style={{
            display: 'flex',
            gap: 0,
            marginBottom: 18,
            borderBottom: '2px solid var(--bd)',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'thin',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
          }}
        >
          <TabBtn t="resumen" label="Resumen" />
          <span data-tour="inicio-tab-metricas">
            <TabBtn t="metricas" label="Métricas principales" />
          </span>
          <span data-tour="inicio-tab-cajitas">
            <TabBtn t="cajitas" label="Stars comerciales" />
          </span>
          <span data-tour="inicio-tab-ranking">
            <TabBtn t="ranking" label="Ranking comisiones" />
          </span>
        </div>

        {activeTab === 'metricas' && (
          <>
            {/* Lote-132: vuelve la "Métricas principales" original — 9 KPIs
                en dos filas. El nuevo dashboard simplificado vive en la tab
                "✨ Resumen (nuevo)" para compararlos hasta decidir cuál
                quedarse. */}
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--tx-mu)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
                margin: '4px 0 10px',
              }}
            >
              Métricas · {rangeLabel}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                gap: 10,
                marginBottom: 12,
              }}
            >
              {initialLoading ? (
                Array.from({ length: 5 }).map((_, i) => <KpiSkeleton key={i} />)
              ) : (
                <>
                  <KpiCard
                    label="Comerciales activos"
                    value={comerciales.length}
                    sub={`${activeComerciales} con comisión > 0`}
                    tooltip="Total de comerciales del scope. Sub-texto cuenta los que registraron ≥1€ de comisión."
                    onClick={() => setDrill('comerciales')}
                  />
                  <KpiCard
                    label="Facturación bruta"
                    value={fmt(billing)}
                    sub={`${billingInfo.count} matrículas`}
                    tooltip="Suma del importe de los deals 'Cierre ganado' con fecha de cierre en el periodo. Bruta."
                    yoyPct={yoyBilling}
                    yoyLabel={yoyLabelFor(priorBilling, fmt)}
                    onClick={() => setDrill('billing')}
                  />
                  {(() => {
                    const monthsInRange = isCustom
                      ? Math.max(1, (customToY - customFromY) * 12 + (customToM - customFromM + 1))
                      : isYear
                        ? 12
                        : 1;
                    const periodMul = isCurrentPeriod && projectionMul > 0 ? projectionMul : 1;
                    const annualPerCom = billingPerCom * periodMul * (12 / monthsInRange);
                    return (
                      <KpiCard
                        label="Facturación / comercial"
                        value={fmt(billingPerCom)}
                        sub={`media · ${owners.size} vendiendo · proyección anual ${fmt(annualPerCom)}`}
                        tooltip="Facturación bruta del periodo dividida entre los comerciales que han vendido al menos 1 deal."
                        onClick={() => setDrill('billing')}
                      />
                    );
                  })()}
                  <KpiCard
                    label="Comisión bruta"
                    value={fmt(commission)}
                    sub={`${comercialMonthEntries.length} registro${comercialMonthEntries.length !== 1 ? 's' : ''}`}
                    tooltip="Suma de comisiones calculadas en el historial del periodo. BRUTA = antes de penalizaciones."
                    yoyPct={yoyCommission}
                    yoyLabel={yoyLabelFor(priorCommission, fmt)}
                    onClick={() => setDrill('commission')}
                  />
                  <KpiCard
                    label="% Comisión / Facturación"
                    value={billing > 0 ? commPct.toFixed(1) + '%' : '—'}
                    sub={billing > 0 ? `${fmt(commission)} sobre ${fmt(billing)}` : 'Sin datos'}
                    tooltip="Ratio comisión bruta / facturación bruta. Típicamente 3-8%."
                    onClick={() => setDrill('commPct')}
                  />
                </>
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                gap: 10,
                marginBottom: 18,
              }}
            >
              {initialLoading ? (
                Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)
              ) : (
                <>
                  <KpiCard
                    label="Bajas aceptadas"
                    value={refundsMonth.length}
                    sub={
                      dealsMonth.length > 0
                        ? ((refundsMonth.length / dealsMonth.length) * 100).toFixed(1) +
                          (isYear ? '% del año' : '% del mes')
                        : '—'
                    }
                    tooltip="Deals con baja aceptada cuya fecha de venta cae en el periodo."
                    alert={refundsMonth.length > 0}
                    yoyPct={yoy(refundsMonth.length, priorRefundsMonth.length)}
                    yoyLabel={
                      priorRefundsMonth.length > 0
                        ? `vs mismo periodo año anterior: ${priorRefundsMonth.length} bajas`
                        : ''
                    }
                    yoyInverse
                    onClick={() => setDrill('refunds')}
                  />
                  <KpiCard
                    label="Impagos firmes 2ª cuota"
                    value={paymentsMonth2.length}
                    sub={paymentsMonth2.length > 0 ? 'penaliza la comisión' : '—'}
                    tooltip="Cuota 2 impagada firme. Descuenta 100% de la comisión."
                    alert={paymentsMonth2.length > 0}
                    onClick={() => setDrill('unpaid2')}
                  />
                  <KpiCard
                    label="Impagos firmes 3ª cuota"
                    value={paymentsMonth3.length}
                    sub={paymentsMonth3.length > 0 ? 'penaliza la comisión' : '—'}
                    tooltip="Cuota 3 impagada firme. Descuenta 50% de la comisión."
                    alert={paymentsMonth3.length > 0}
                    onClick={() => setDrill('unpaid3')}
                  />
                  <KpiCard
                    label="Pago neto"
                    value={fmt(finalPayout)}
                    sub={adjustments < 0 ? `Ajustes: ${fmt(adjustments)}` : 'Sin ajustes'}
                    tooltip="Comisión final tras aplicar bajas e impagos."
                    onClick={() => setDrill('netPay')}
                  />
                </>
              )}
            </div>

            {/* ── Records: tarjeta full-width (Recomendaciones eliminadas) ── */}
            <div style={{ marginBottom: 18 }}>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="ch" style={{ borderBottom: '1px solid var(--bds)' }}>
                  <h3>🥇 Records · {rangeLabel}</h3>
                  <span className="pill pill-pu">{records.length}</span>
                </div>
                {records.length === 0 ? (
                  <div
                    style={{
                      padding: '24px 18px',
                      fontSize: 12,
                      color: 'var(--tx-mu)',
                      textAlign: 'center',
                    }}
                  >
                    Sin datos suficientes en este periodo.
                  </div>
                ) : (
                  <div>
                    {records.map((r, i) => (
                      <div
                        key={i}
                        onClick={() => setRecordModal(r)}
                        style={{
                          padding: '10px 16px',
                          borderBottom: '1px solid var(--bds)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          cursor: 'pointer',
                          transition: 'background .1s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                        onMouseLeave={e => (e.currentTarget.style.background = '')}
                      >
                        <div style={{ fontSize: 18, flexShrink: 0 }}>{r.icon}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            {r.label}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--tx2)',
                              marginTop: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={r.sub}
                          >
                            {r.sub}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: 'var(--tx)',
                            flexShrink: 0,
                          }}
                        >
                          {r.value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Calendario · próximos eventos del equipo. Aparece como tarjeta única
          full-width debajo de Recomendaciones+Records, sólo si hay events. */}
            <EventsCalendar
              isManager={!lockedTeamId}
              teams={teams || []}
              viewerTeamId={lockedTeamId || null}
            />
          </>
        )}

        {activeTab === 'resumen' && (
          <>
            {/* Lote-132: dashboard manager simplificado (hero + secundarias).
                Mantenemos esto como tab paralela a "Métricas principales" para
                comparar formatos. Si se confirma, retiramos la tab vieja. */}
            {initialLoading ? (
              <div style={{ marginBottom: 16 }}>
                <KpiSkeleton />
              </div>
            ) : (
              (() => {
                // Lote-134: prioridad de objetivo en el hero —
                //   1. Consolidado del equipo seleccionado para el mes activo
                //      (configurado en la tabla "Objetivos consolidados de equipo")
                //   2. Si no hay y el modo es Año: suma de los 12 meses consolidados
                //   3. Fallback: suma de objetivos individuales de comerciales
                //      (lo que hacía antes — incluye solo lo que cada comercial
                //      tiene como meta personal, sin B2B).
                const sumIndividual = comerciales.reduce(
                  (s, u) => s + (parseFloat(objectiveOf(u)) || 0),
                  0
                );
                let teamObjective = 0;
                let objSource = 'none';
                if (teamFilter) {
                  // Equipo concreto seleccionado → buscamos su consolidado
                  if (isYear) {
                    teamObjective = (teamObjectives || [])
                      .filter(o => o.teamId === teamFilter && !o.subteamId && o.year === selYear)
                      .reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
                  } else {
                    const row = (teamObjectives || []).find(
                      o =>
                        o.teamId === teamFilter &&
                        !o.subteamId &&
                        o.year === selYear &&
                        o.month === selMonth
                    );
                    teamObjective = row ? parseFloat(row.amount) || 0 : 0;
                  }
                  if (teamObjective > 0) objSource = 'consolidated';
                } else {
                  // Vista global (todos los equipos) → suma de consolidados de
                  // todos los equipos padre. Si no hay, fallback a individuales.
                  const tobjs = teamObjectives || [];
                  if (isYear) {
                    teamObjective = tobjs
                      .filter(o => !o.subteamId && o.year === selYear)
                      .reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
                  } else {
                    teamObjective = tobjs
                      .filter(o => !o.subteamId && o.year === selYear && o.month === selMonth)
                      .reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
                  }
                  if (teamObjective > 0) objSource = 'consolidated';
                }
                if (teamObjective <= 0 && sumIndividual > 0) {
                  teamObjective = sumIndividual;
                  objSource = 'individual';
                }
                const pct =
                  teamObjective > 0 ? Math.min(150, (billing / teamObjective) * 100) : null;
                const pctColor =
                  pct == null
                    ? 'var(--tx-mu)'
                    : pct >= 100
                      ? 'var(--gr)'
                      : pct >= 70
                        ? 'var(--or)'
                        : 'var(--rd)';
                const projected =
                  isCurrentPeriod && projectionMul > 1 ? billing * projectionMul : null;
                return (
                  <div
                    onClick={() => setDrill('billing')}
                    style={{
                      background:
                        'linear-gradient(135deg, var(--pu-d, #5e3aa8) 0%, var(--pu, #7c5cd1) 100%)',
                      color: '#fff',
                      borderRadius: 16,
                      padding: '20px 24px',
                      marginBottom: 16,
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(0,0,0,.08)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          opacity: 0.85,
                          textTransform: 'uppercase',
                          letterSpacing: '.5px',
                          fontWeight: 600,
                        }}
                      >
                        Facturación bruta · {rangeLabel}
                      </div>
                      {yoyBilling != null && (
                        <div
                          style={{ fontSize: 12, opacity: 0.85 }}
                          title={yoyLabelFor(priorBilling, fmt)}
                        >
                          {yoyBilling >= 0 ? '↑' : '↓'} {Math.abs(yoyBilling).toFixed(1)}% vs año
                          anterior
                        </div>
                      )}
                    </div>
                    <div
                      style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.1, marginBottom: 4 }}
                    >
                      {fmt(billing)}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 14 }}>
                      {billingInfo.count} matrículas
                      {projected != null && (
                        <>
                          {' · '}
                          <span style={{ opacity: 0.95 }}>
                            proyección {fmt(projected)} a fin de {isYear ? 'año' : 'mes'}
                          </span>
                        </>
                      )}
                    </div>
                    {teamObjective > 0 ? (
                      <div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 12,
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ opacity: 0.9 }}>
                            {objSource === 'individual'
                              ? `Objetivo (suma individuales): ${fmt(teamObjective)}`
                              : `Objetivo equipo: ${fmt(teamObjective)}`}
                          </span>
                          <span style={{ fontWeight: 700 }}>{pct.toFixed(0)}%</span>
                        </div>
                        <div
                          style={{
                            height: 8,
                            background: 'rgba(255,255,255,.2)',
                            borderRadius: 4,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: Math.min(100, pct) + '%',
                              background: pctColor === 'var(--gr)' ? '#5fd9a1' : '#ffd28a',
                              transition: 'width .3s',
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, opacity: 0.7, fontStyle: 'italic' }}>
                        Sin objetivo de equipo configurado · añádelo en la tab "Equipo vs Objetivo"
                        para ver el progreso aquí
                      </div>
                    )}
                    {/* Lote-134: desglose comerciales vs B2B/residual cuando
                        el objetivo es consolidado (sabemos suma individuales). */}
                    {objSource === 'consolidated' && sumIndividual > 0 && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: '8px 12px',
                          background: 'rgba(255,255,255,.1)',
                          borderRadius: 6,
                          fontSize: 11,
                          opacity: 0.9,
                          lineHeight: 1.6,
                        }}
                      >
                        <div>· Comerciales (individuales): meta {fmt(sumIndividual)}</div>
                        <div>
                          · Resto (B2B / residual): meta{' '}
                          {fmt(Math.max(0, teamObjective - sumIndividual))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                gap: 10,
                marginBottom: 12,
              }}
            >
              {initialLoading ? (
                Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)
              ) : (
                <>
                  <KpiCard
                    label="Comerciales activos"
                    value={comerciales.length}
                    sub={`${activeComerciales} con comisión > 0`}
                    onClick={() => setDrill('comerciales')}
                  />
                  <KpiCard
                    label="Comisión bruta"
                    value={fmt(commission)}
                    sub={`${comercialMonthEntries.length} reg.`}
                    yoyPct={yoyCommission}
                    yoyLabel={yoyLabelFor(priorCommission, fmt)}
                    onClick={() => setDrill('commission')}
                  />
                  <KpiCard
                    label="Bajas aceptadas"
                    value={refundsMonth.length}
                    sub={
                      dealsMonth.length > 0
                        ? ((refundsMonth.length / dealsMonth.length) * 100).toFixed(1) +
                          (isYear ? '% año' : '% mes')
                        : '—'
                    }
                    alert={refundsMonth.length > 0}
                    yoyPct={yoy(refundsMonth.length, priorRefundsMonth.length)}
                    yoyInverse
                    onClick={() => setDrill('refunds')}
                  />
                  <KpiCard
                    label="Pago neto"
                    value={fmt(finalPayout)}
                    sub={adjustments < 0 ? `Ajustes: ${fmt(adjustments)}` : 'Sin ajustes'}
                    onClick={() => setDrill('netPay')}
                  />
                </>
              )}
            </div>

            {/* lote-182 (1.2): acciones pendientes del mes (izq) + info
                relevante vs mes anterior (der). Solo en vista mes. */}
            {showOpsPanels && !initialLoading && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                {/* IZQUIERDA — Acciones pendientes del mes */}
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      padding: '10px 14px',
                      borderBottom: '1px solid var(--bds)',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    ✅ Acciones pendientes ·{' '}
                    <span style={{ color: 'var(--tx-mu)' }}>
                      {MONTHS[selMonth - 1]} {selYear}
                    </span>
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    <ActionRow
                      done={objetivosOk}
                      title="Objetivos del equipo"
                      detail={
                        comerciales.length === 0
                          ? 'Sin comerciales en el equipo'
                          : objetivosOk
                            ? `${comerciales.length}/${comerciales.length} comerciales con objetivo`
                            : `${comerciales.length - comercialesSinObjetivo.length}/${comerciales.length} con objetivo · faltan ${comercialesSinObjetivo.length}`
                      }
                      onClick={() => goTo('team-objectives')}
                    />
                    <ActionRow
                      done={mesCerrado}
                      title="Cierre del mes"
                      detail={mesCerrado ? 'Mes cerrado' : 'Pendiente de cerrar'}
                      onClick={() => goTo('month-closure')}
                    />
                    {teamFilter && (
                      <ActionRow
                        done={!!confirmacionMes}
                        title="Confirmar tramos y equipo"
                        detail={
                          confirmacionMes
                            ? `Confirmado ${new Date(confirmacionMes.confirmedAt).toLocaleDateString('es-ES')}${confirmacionMes.confirmedByName ? ' · ' + confirmacionMes.confirmedByName : ''}`
                            : 'Revisa que tramos y equipo siguen igual'
                        }
                        action={
                          !confirmacionMes ? (
                            <button
                              className="btn btn-pu btn-sm"
                              disabled={confirming}
                              onClick={confirmTramos}
                            >
                              {confirming ? '…' : 'Confirmar'}
                            </button>
                          ) : null
                        }
                      />
                    )}
                  </div>
                </div>

                {/* DERECHA — Info relevante vs mes anterior */}
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      padding: '10px 14px',
                      borderBottom: '1px solid var(--bds)',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    📌 Info relevante ·{' '}
                    <span style={{ color: 'var(--tx-mu)' }}>vs {MONTHS[pmM - 1]}</span>
                  </div>
                  <div
                    style={{
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <InfoRow
                      label="Comerciales"
                      value={String(comerciales.length)}
                      delta={deltaComerciales}
                      onClick={() => setInfoDetail('comerciales')}
                    />
                    <InfoRow
                      label={`Comisión bruta · ${MONTHS[pmM - 1]}`}
                      value={fmt(prevBrutaTotal)}
                      sub={`media ${fmt(prevBrutaAvg)} · ${prevActivos} con comisión`}
                      onClick={prevByComercial.length ? () => setInfoDetail('bruta') : undefined}
                    />
                    <InfoRow
                      label={`Comisión neta · ${MONTHS[pmM - 1]}`}
                      value={fmt(prevNetaTotal)}
                      sub={`media ${fmt(prevNetaAvg)}`}
                      onClick={prevByComercial.length ? () => setInfoDetail('neta') : undefined}
                    />
                    <InfoRow
                      label="Cambios de tramo"
                      value={tramoChanges.length === 0 ? 'Ninguno' : String(tramoChanges.length)}
                      sub={tramoChanges
                        .slice(0, 3)
                        .map(c => c.name)
                        .join(', ')}
                      onClick={tramoChanges.length ? () => setInfoDetail('tramo') : undefined}
                    />
                    <InfoRow
                      label="Cambios de equipo"
                      value={equipoChanges.length === 0 ? 'Ninguno' : String(equipoChanges.length)}
                      sub={equipoChanges
                        .slice(0, 3)
                        .map(c => c.name)
                        .join(', ')}
                      onClick={equipoChanges.length ? () => setInfoDetail('equipo') : undefined}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* lote-187: desglose al pulsar una fila de "Info relevante". */}
            {infoDetail && (
              <InfoDetailModal
                kind={infoDetail}
                onClose={() => setInfoDetail(null)}
                pmLabel={MONTHS[pmM - 1]}
                altas={altasComerciales}
                bajas={bajasComerciales}
                current={comerciales}
                prevByComercial={prevByComercial}
                prevBrutaTotal={prevBrutaTotal}
                prevNetaTotal={prevNetaTotal}
                tramoChanges={tramoChanges}
                equipoChanges={equipoChanges}
              />
            )}

            {/* lote-181 (1.1): desglose por subequipo de la vertical. El equipo
                del TL que mira se resalta. Sustituye al antiguo "ver más
                métricas" (1.3, retirado). */}
            {!initialLoading && subteamRows.length > 0 && (
              <div className="card" style={{ marginBottom: 18, overflow: 'hidden' }}>
                <div
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--bds)',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  🧩 Por subequipo · <span style={{ color: 'var(--tx-mu)' }}>{rangeLabel}</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr
                        style={{
                          color: 'var(--tx-mu)',
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '.4px',
                        }}
                      >
                        <th style={{ textAlign: 'left', padding: '6px 14px' }}>Subequipo</th>
                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Facturación</th>
                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Objetivo</th>
                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Comerciales</th>
                        <th style={{ textAlign: 'right', padding: '6px 14px' }}>Comisión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subteamRows.map(r => {
                        const mine = r.id === mySubteamId;
                        return (
                          <tr
                            key={r.id}
                            style={{
                              borderTop: '1px solid var(--bds)',
                              background: mine ? 'var(--pu-bgl, #faf7ff)' : undefined,
                            }}
                          >
                            <td
                              style={{
                                padding: '8px 14px',
                                fontWeight: mine ? 800 : 600,
                                color: mine ? 'var(--pu)' : 'var(--tx)',
                              }}
                            >
                              {r.name}
                              {mine && (
                                <span
                                  className="pill pill-pu"
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    padding: '1px 6px',
                                  }}
                                >
                                  TU EQUIPO
                                </span>
                              )}
                            </td>
                            <td
                              style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}
                            >
                              {fmt(r.billing)}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              {r.objetivo > 0 ? (
                                <>
                                  {fmt(r.objetivo)}{' '}
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color:
                                        r.pct >= 100
                                          ? 'var(--gr-d)'
                                          : r.pct >= 75
                                            ? 'var(--wn)'
                                            : 'var(--er)',
                                    }}
                                  >
                                    · {r.pct}%
                                  </span>
                                </>
                              ) : (
                                <span style={{ color: 'var(--tx-mu)' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.count}</td>
                            <td
                              style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600 }}
                            >
                              {fmt(r.commission)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'cajitas' && (
          <>
            {/* Cards "FIFA" — una tarjeta por comercial con sus 6 métricas del periodo.
          Se muestran sólo los comerciales con al menos 1 venta en el scope; el
          filtro de equipo del topbar afecta directamente. Click → perfil. */}
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--tx-mu)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
                margin: '4px 0 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span>Comerciales · {isYear ? selYear : MONTHS[selMonth - 1] + ' ' + selYear}</span>
              <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--tx-mu)' }}>
                · {cards.length} con ventas
              </span>
            </div>
            {cards.length === 0 ? (
              <div className="card" style={{ padding: '24px 20px', marginBottom: 18 }}>
                <div className="em">
                  <h3>Sin ventas registradas</h3>
                  <p>
                    No hay deals en {isYear ? selYear : MONTHS[selMonth - 1] + ' ' + selYear} para
                    este scope.
                  </p>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))',
                  gap: 10,
                  marginBottom: 18,
                }}
              >
                {cards.map(c => {
                  const team = (teams || []).find(t => t.id === c.teamId);
                  // Card abre el drawer con el desglose (comisiones + bajas + ventas)
                  // del comercial sobre el periodo activo. Mantenemos userObj para
                  // que el drawer pueda enlazar al perfil completo si hace falta.
                  const userObj = c.userId ? allUsers.find(u => u.id === c.userId) : null;
                  const clickable = true;
                  const openDetail = () => setDetailComercial({ card: c, user: userObj });
                  return (
                    <div
                      key={c.key}
                      className="card"
                      style={{
                        padding: '14px 14px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'transform .12s, box-shadow .12s',
                      }}
                      onClick={openDetail}
                      onMouseEnter={
                        clickable
                          ? e => {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.06)';
                            }
                          : undefined
                      }
                      onMouseLeave={
                        clickable
                          ? e => {
                              e.currentTarget.style.transform = '';
                              e.currentTarget.style.boxShadow = '';
                            }
                          : undefined
                      }
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <div
                          className="uav com"
                          style={{ width: 30, height: 30, fontSize: 12, flex: '0 0 30px' }}
                        >
                          {(c.name || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: 'var(--tx)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={c.name}
                          >
                            {c.name}
                          </div>
                          {team && (
                            <div
                              style={{
                                fontSize: 10,
                                color: 'var(--tx-mu)',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '.4px',
                              }}
                            >
                              {team.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          borderTop: '1px solid var(--bds)',
                          paddingTop: 8,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 6,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Facturación
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                            {fmt(c.billing)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Ventas
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                            {c.sales}
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Com. bruta
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                            {fmt(c.gross)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Com. neta
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: c.net > 0 ? 'var(--pu)' : 'var(--tx-mu)',
                            }}
                          >
                            {fmt(c.net)}
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Bajas
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: c.refunds > 0 ? 'var(--er)' : 'var(--tx-mu)',
                            }}
                          >
                            {c.refunds}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Impagos
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: c.unpaid > 0 ? 'var(--or)' : 'var(--tx-mu)',
                            }}
                          >
                            {c.unpaid}
                          </div>
                        </div>
                        {/* Objetivo: si está sin configurar muestra "—". */}
                        <div>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Objetivo
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                            {c.objective == null || c.objective === 0 ? '—' : fmt(c.objective)}
                          </div>
                        </div>
                        {/* Proyección: regla de tres días transcurridos / totales del periodo.
                      Solo se muestra si estamos en el periodo en curso. */}
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: 'var(--tx-mu)',
                              textTransform: 'uppercase',
                              letterSpacing: '.4px',
                            }}
                          >
                            Proyección
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                            {isCurrentPeriod && projectionMul > 0
                              ? fmt(c.billing * projectionMul)
                              : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'ranking' && (
          <>
            {/* Ranking de comisiones del periodo. Estilo de la tabla del modal de
          records: posición + comercial (con facturación como subtítulo) +
          columnas alineadas a la derecha. Solo role=comercial. */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="ch" style={{ borderBottom: '1px solid var(--bds)' }}>
                <h3>🏆 Ranking comisiones · {rangeLabel}</h3>
                <span className="pill pill-pu">{ranking.length}</span>
              </div>
              {ranking.length === 0 ? (
                <div
                  style={{
                    padding: '24px 18px',
                    fontSize: 12,
                    color: 'var(--tx-mu)',
                    textAlign: 'center',
                  }}
                >
                  Sin comisiones registradas en este periodo.
                </div>
              ) : (
                <table className="hs-tbl" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 42, textAlign: 'center' }}>#</th>
                      <th>Comercial</th>
                      <th style={{ textAlign: 'right' }}>Ventas</th>
                      <th style={{ textAlign: 'right' }}>Com. bruta</th>
                      <th style={{ textAlign: 'right' }}>Com. neta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((r, i) => {
                      const name = r.user?.name || r.userName || '—';
                      const clickable = r.user && setProfileUser && goTo;
                      return (
                        <tr
                          key={r.userId || r.refId || i}
                          style={{ cursor: clickable ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (clickable) {
                              setProfileUser(r.user);
                              goTo('users');
                            }
                          }}
                        >
                          <td
                            style={{
                              width: 42,
                              textAlign: 'center',
                              fontWeight: 700,
                              color: i < 3 ? 'var(--pu)' : 'var(--tx-mu)',
                              fontSize: 13,
                            }}
                          >
                            {i + 1}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div
                                className="uav com"
                                style={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}
                              >
                                {(name[0] || '?').toUpperCase()}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                                  {name}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 1 }}>
                                  {r.billing > 0
                                    ? `${fmt(r.billing)} facturado`
                                    : 'Sin facturación registrada'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--tx-mu)' }}>
                            {r.sales || '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
                            {fmt(r.comm)}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontSize: 13,
                              fontWeight: 700,
                              color: r.net > 0 ? 'var(--pu)' : 'var(--tx-mu)',
                            }}
                          >
                            {fmt(r.net)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Lote-155: la pestaña "Equipo vs Objetivo" se movió a su propia
            página "Objetivos" dentro de "Mi equipo" (src/objetivos.jsx). */}
      </div>

      {/* Modal lateral con detalle del KPI clickado. */}
      {drill && (
        <DrillModal
          drill={drill}
          onClose={() => setDrill(null)}
          selMonth={selMonth}
          selYear={selYear}
          isYear={isYear}
          cards={cards}
          comerciales={comerciales}
          activeComerciales={activeComerciales}
          teams={teams}
          billing={billing}
          billingInfo={billingInfo}
          commission={commission}
          commPct={commPct}
          refundsMonth={refundsMonth}
          paymentsMonth2={paymentsMonth2}
          paymentsMonth3={paymentsMonth3}
          finalPayout={finalPayout}
          adjustments={adjustments}
          monthEntries={monthEntries}
          allUsers={allUsers}
          ownerById={ownerById}
          dealById={dealById}
          setProfileUser={setProfileUser}
          goTo={goTo}
          isManager={isManager}
          onOpenDiagnose={() => setRefundDiagnoseOpen(true)}
        />
      )}

      {/* Modal con el top 10 del record clicado */}
      {recordModal && (
        <div
          onClick={() => setRecordModal(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(20,20,20,.45)',
            zIndex: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--wh)',
              borderRadius: 14,
              width: 'min(620px,96vw)',
              maxHeight: '82vh',
              boxShadow: '0 8px 32px rgba(0,0,0,.2)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '16px 22px',
                borderBottom: '1px solid var(--bds)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ fontSize: 28, flexShrink: 0 }}>{recordModal.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--tx-mu)',
                    textTransform: 'uppercase',
                    letterSpacing: '.4px',
                  }}
                >
                  Top 10 · {rangeLabel}
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
                  {recordModal.label}
                </div>
              </div>
              <button className="btn btn-gh btn-ic" onClick={() => setRecordModal(null)}>
                {Ic.close}
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {(recordModal.rows || []).length === 0 ? (
                <div
                  style={{
                    padding: '24px 22px',
                    fontSize: 12,
                    color: 'var(--tx-mu)',
                    textAlign: 'center',
                  }}
                >
                  Sin datos.
                </div>
              ) : (
                <table className="hs-tbl" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36, textAlign: 'center' }}>#</th>
                      {recordModal.columns.map(col => (
                        <th key={col.key} style={{ textAlign: col.right ? 'right' : 'left' }}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recordModal.rows.map((row, i) => (
                      <tr key={row.key || i}>
                        <td
                          style={{
                            width: 36,
                            textAlign: 'center',
                            fontWeight: 700,
                            color: i < 3 ? 'var(--pu)' : 'var(--tx-mu)',
                          }}
                        >
                          {i + 1}
                        </td>
                        {recordModal.columns.map(col => (
                          <td
                            key={col.key}
                            style={{
                              textAlign: col.right ? 'right' : 'left',
                              fontWeight: col.right ? 600 : 500,
                              fontSize: 12,
                            }}
                          >
                            {row[col.key] != null ? row[col.key] : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lote-121: modal de diagnóstico de bajas. Útil para investigar
          "esta baja no se cuenta" o "esta sale duplicada" — reproduce
          exactamente la lógica de scope (team/period) de Inicio. */}
      {refundDiagnoseOpen && (
        <div
          onClick={() => setRefundDiagnoseOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(20,20,20,.45)',
            zIndex: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--wh)',
              borderRadius: 14,
              width: 'min(820px,96vw)',
              maxHeight: '85vh',
              boxShadow: '0 8px 32px rgba(0,0,0,.2)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--bds)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 22 }}>🔍</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>Diagnosticar baja</h3>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--tx-mu)' }}>
                  Escribe parte del nombre del comercial o del cliente. Te diré por qué cada baja
                  matching aparece o no en esta vista.
                </p>
              </div>
              <button
                onClick={() => setRefundDiagnoseOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  color: 'var(--tx-mu)',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input
                  type="text"
                  className="fs"
                  placeholder='Ej: "piaget", "rihab", "mounir"'
                  value={refundDiagnoseQuery}
                  onChange={e => setRefundDiagnoseQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') runRefundDiagnose();
                  }}
                  style={{ flex: 1, fontSize: 13, padding: '6px 10px' }}
                  autoFocus
                />
                <button
                  className="btn"
                  onClick={runRefundDiagnose}
                  disabled={refundDiagnoseLoading || !refundDiagnoseQuery.trim()}
                  style={{ fontSize: 12, padding: '6px 14px' }}
                >
                  {refundDiagnoseLoading ? 'Buscando…' : 'Buscar'}
                </button>
              </div>
              {refundDiagnoseResult?.error && (
                <div
                  style={{
                    padding: 10,
                    background: 'var(--er-bg, #fee)',
                    border: '1px solid var(--er, #c33)',
                    borderRadius: 6,
                    fontSize: 12,
                    color: 'var(--er, #900)',
                  }}
                >
                  {refundDiagnoseResult.error}
                </div>
              )}
              {refundDiagnoseResult?.refunds && (
                <>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--tx-mu)',
                      marginBottom: 10,
                    }}
                  >
                    {refundDiagnoseResult.summary.total} bajas matching ·{' '}
                    <span style={{ color: 'var(--ok, #2a7)' }}>
                      {refundDiagnoseResult.summary.included} incluidas
                    </span>{' '}
                    ·{' '}
                    <span style={{ color: 'var(--er, #c33)' }}>
                      {refundDiagnoseResult.summary.excluded} excluidas
                    </span>{' '}
                    en esta vista ({refundDiagnoseResult.filters.year || 'cualquier año'}
                    {refundDiagnoseResult.filters.month
                      ? ` · mes ${refundDiagnoseResult.filters.month}`
                      : ''}
                    {refundDiagnoseResult.filters.teamId ? ` · equipo seleccionado` : ''})
                  </div>
                  {refundDiagnoseResult.refunds.length === 0 ? (
                    <p style={{ fontSize: 12, color: 'var(--tx-mu)' }}>
                      Sin bajas matching el nombre. Prueba con otra parte (ej. solo apellido).
                    </p>
                  ) : (
                    <table className="hs-tbl" style={{ width: '100%', fontSize: 11 }}>
                      <thead>
                        <tr>
                          <th>Cliente</th>
                          <th>Comercial</th>
                          <th>Cierre</th>
                          <th>Equipo</th>
                          <th>¿En vista?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refundDiagnoseResult.refunds.map((r, i) => (
                          <tr key={i}>
                            <td style={{ maxWidth: 180 }}>
                              <div style={{ fontWeight: 600 }}>{r.dealName || '—'}</div>
                              <div style={{ fontSize: 10, color: 'var(--tx-mu)' }}>
                                {r.ticketSubject || ''}
                              </div>
                            </td>
                            <td>{r.ownerName || r.ownerId || '—'}</td>
                            <td style={{ color: 'var(--tx-mu)' }}>
                              {r.closedate ? r.closedate.slice(0, 10) : '—'}
                            </td>
                            <td>
                              <div>
                                {r.teamResolvedName || (
                                  <span style={{ color: 'var(--er)' }}>
                                    {r.teamResolved === null ? 'NULL (WorkCamp)' : '—'}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--tx-mu)' }}>
                                {r.teamSource}
                              </div>
                            </td>
                            <td>
                              {r.includedInView ? (
                                <span style={{ color: 'var(--ok, #2a7)', fontWeight: 600 }}>
                                  ✓ sí
                                </span>
                              ) : (
                                <div>
                                  <span style={{ color: 'var(--er, #c33)', fontWeight: 600 }}>
                                    ✗ no
                                  </span>
                                  <div style={{ fontSize: 10, color: 'var(--tx-mu)' }}>
                                    {r.reasons.join(', ')}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drawer con el desglose de un comercial al clicar su cajita */}
      {detailComercial && (
        <ComercialDetailDrawer
          detail={detailComercial}
          onClose={() => setDetailComercial(null)}
          monthEntries={monthEntries}
          refundsMonth={refundsMonth}
          paymentsMonth={paymentsMonth}
          dealsMonth={dealsMonth}
          dealById={dealById}
          rangeLabel={rangeLabel}
          teams={teams}
          setProfileUser={setProfileUser}
          goTo={goTo}
        />
      )}
    </>
  );
}

export function ComercialDetailDrawer({
  detail,
  onClose,
  monthEntries,
  refundsMonth,
  paymentsMonth,
  dealsMonth,
  dealById,
  rangeLabel,
  teams,
  setProfileUser,
  goTo,
}) {
  const { card: c, user } = detail;
  // Filtramos los datos al ownerId/userId de este comercial.
  const myEntries = (monthEntries || []).filter(
    h => (c.userId && h.userId === c.userId) || (c.ownerId && h.ownerId === c.ownerId)
  );
  const myDeals = (dealsMonth || []).filter(d => c.ownerId && d.ownerId === c.ownerId);
  const myRefunds = (refundsMonth || []).filter(r => c.ownerId && r.ownerId === c.ownerId);
  const myPayments = (paymentsMonth || []).filter(p => {
    const d = dealById[p.dealId];
    return d && c.ownerId && d.ownerId === c.ownerId;
  });
  const team = (teams || []).find(t => t.id === c.teamId);
  const totalBilling = myDeals.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const totalGross = myEntries.reduce((s, h) => s + (h.result?.totalCommission || 0), 0);
  const totalNet = myEntries.reduce(
    (s, h) => s + (h.finalPayout != null ? h.finalPayout : h.result?.totalCommission || 0),
    0
  );

  // Sección · Bloque colapsable reutilizable.
  const Section = ({ title, count, defaultOpen = true, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
      <div style={{ borderBottom: '1px solid var(--bds)' }}>
        <div
          onClick={() => setOpen(o => !o)}
          style={{
            padding: '12px 22px',
            background: 'var(--bg)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            userSelect: 'none',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            style={{
              width: 13,
              height: 13,
              color: 'var(--tx-mu)',
              transition: 'transform .15s',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.4px',
              color: 'var(--tx)',
            }}
          >
            {title}
          </span>
          {count != null && (
            <span className="pill pill-pu" style={{ marginLeft: 6 }}>
              {count}
            </span>
          )}
        </div>
        {open && <div>{children}</div>}
      </div>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,20,20,.35)',
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(680px,96vw)',
          height: '100vh',
          background: 'var(--wh)',
          overflow: 'auto',
          boxShadow: '-8px 0 24px rgba(0,0,0,.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--bd)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            background: 'var(--wh)',
            zIndex: 1,
          }}
        >
          <div className="uav com" style={{ width: 44, height: 44, fontSize: 18, flexShrink: 0 }}>
            {(c.name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{c.name}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 2 }}>
              {team ? team.name + ' · ' : ''}
              {rangeLabel}
            </div>
          </div>
          {user && setProfileUser && goTo && (
            <button
              className="btn btn-gh btn-sm"
              onClick={() => {
                setProfileUser(user);
                goTo('users');
                onClose();
              }}
              title="Abrir perfil completo"
            >
              Ver perfil →
            </button>
          )}
          <button className="btn btn-gh btn-ic" onClick={onClose} style={{ flexShrink: 0 }}>
            {Ic.close}
          </button>
        </div>

        {/* Sumario rápido */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 1,
            background: 'var(--bds)',
            borderBottom: '1px solid var(--bds)',
          }}
        >
          {[
            {
              l: 'Facturación',
              v: fmt(totalBilling),
              s: `${myDeals.length} venta${myDeals.length !== 1 ? 's' : ''}`,
            },
            {
              l: 'Comisión bruta',
              v: fmt(totalGross),
              s: `${myEntries.length} cálculo${myEntries.length !== 1 ? 's' : ''}`,
            },
            {
              l: 'Comisión neta',
              v: fmt(totalNet),
              s: c.objective > 0 ? `Objetivo: ${fmt(c.objective)}` : 'Sin objetivo',
            },
          ].map((x, i) => (
            <div key={i} style={{ background: 'var(--wh)', padding: '14px 18px' }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                {x.l}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 3, letterSpacing: '-.3px' }}>
                {x.v}
              </div>
              <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 2 }}>{x.s}</div>
            </div>
          ))}
        </div>

        {/* ── COMISIONES ── */}
        <Section title="Comisiones" count={myEntries.length}>
          {myEntries.length === 0 ? (
            <div style={{ padding: '18px 22px', fontSize: 12, color: 'var(--tx-mu)' }}>
              Sin cálculo de comisión guardado en este periodo.
            </div>
          ) : (
            myEntries.map((h, idx) => {
              const r = h.result || {};
              const finalPay = h.finalPayout != null ? h.finalPayout : r.totalCommission || 0;
              const adjustedDown = finalPay < (r.totalCommission || 0) - 0.01;
              return (
                <div
                  key={h.id || idx}
                  style={{
                    padding: '14px 22px',
                    borderTop: idx > 0 ? '1px solid var(--bds)' : 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: 8,
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{h.categoryName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 2 }}>
                        {MONTHS[h.month - 1]} {h.year} · guardado{' '}
                        {new Date(h.savedAt).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--tx-mu)',
                          textTransform: 'uppercase',
                          letterSpacing: '.4px',
                        }}
                      >
                        Comisión final
                      </div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: adjustedDown ? 'var(--er)' : 'var(--pu)',
                          letterSpacing: '-.3px',
                        }}
                      >
                        {fmt(finalPay)}
                      </div>
                    </div>
                  </div>
                  {/* Facturación por cuotas */}
                  {Object.keys(r.billingByKey || {}).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--tx-mu)',
                          marginBottom: 5,
                          textTransform: 'uppercase',
                          letterSpacing: '.3px',
                        }}
                      >
                        Facturación por cuotas
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {Object.entries(r.billingByKey)
                          .filter(([, v]) => v > 0)
                          .map(([k, v]) => {
                            const base = r.baseByKey?.[k] || 0;
                            const pct = v > 0 ? Math.round((base / v) * 100) : 0;
                            return (
                              <div
                                key={k}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  fontSize: 12,
                                }}
                              >
                                <span
                                  style={{
                                    background: 'var(--bl-bg)',
                                    color: 'var(--bl)',
                                    borderRadius: 6,
                                    padding: '1px 7px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}
                                >
                                  {k}x
                                </span>
                                <span style={{ color: 'var(--tx2)', fontWeight: 600 }}>
                                  {fmt(v)}
                                </span>
                                <span style={{ color: 'var(--tx3)' }}>×{pct}%</span>
                                <span style={{ color: 'var(--tx3)' }}>→</span>
                                <span style={{ fontWeight: 600, color: 'var(--tx)' }}>
                                  {fmt(base)}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  {/* Step grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: '3px 12px',
                      fontSize: 12,
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: '1px dashed var(--bds)',
                    }}
                  >
                    <span style={{ color: 'var(--tx-mu)' }}>Base total</span>
                    <span style={{ fontWeight: 600, textAlign: 'right' }}>
                      {fmt(r.totalBase || 0)}
                    </span>
                    {r.matchedRange && (
                      <>
                        <span style={{ color: 'var(--tx-mu)' }}>
                          Tramo {r.matchedRange.min}–
                          {r.matchedRange.max && parseFloat(r.matchedRange.max) > 0
                            ? r.matchedRange.max
                            : '∞'}{' '}
                          ({r.matchedRange.percentTotal}%)
                        </span>
                        <span
                          style={{
                            fontWeight: 600,
                            color: r.rangeExtra >= 0 ? 'var(--gr)' : 'var(--rd)',
                            textAlign: 'right',
                          }}
                        >
                          {r.rangeExtra >= 0 ? '+' : ''}
                          {fmt(r.rangeExtra || 0)}
                        </span>
                      </>
                    )}
                    {r.matchedFinance && (
                      <>
                        <span style={{ color: 'var(--tx-mu)' }}>
                          Financiación · {r.matchedFinance.name}
                        </span>
                        <span
                          style={{
                            fontWeight: 600,
                            color: (r.financeModifier || 0) >= 0 ? 'var(--gr)' : 'var(--rd)',
                            textAlign: 'right',
                          }}
                        >
                          {(r.financeModifier || 0) >= 0 ? '+' : ''}
                          {fmt(r.financeModifier || 0)}
                        </span>
                      </>
                    )}
                    <span style={{ fontWeight: 700, color: 'var(--tx)' }}>Comisión bruta</span>
                    <span style={{ fontWeight: 700, textAlign: 'right' }}>
                      {fmt(r.totalCommission || 0)}
                    </span>
                    {adjustedDown && (
                      <>
                        <span style={{ color: 'var(--er)' }}>Ajustes (bajas / impagos)</span>
                        <span style={{ fontWeight: 700, color: 'var(--er)', textAlign: 'right' }}>
                          {fmt(finalPay - (r.totalCommission || 0))}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Ajustes detalle */}
                  {(h.adjustments || []).length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--tx-mu)' }}>
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 3,
                          textTransform: 'uppercase',
                          letterSpacing: '.3px',
                        }}
                      >
                        Ajustes ({h.adjustments.length})
                      </div>
                      {h.adjustments.map((a, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '2px 0',
                          }}
                        >
                          <span>
                            {a.type === 'refund' ? 'Baja' : 'Impago'} ·{' '}
                            {a.note || a.dealName || '—'}
                          </span>
                          <span style={{ fontWeight: 600, color: 'var(--er)' }}>
                            {fmt(a.amount || 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Section>

        {/* ── BAJAS ── */}
        <Section title="Bajas" count={myRefunds.length} defaultOpen={myRefunds.length > 0}>
          {myRefunds.length === 0 ? (
            <div style={{ padding: '18px 22px', fontSize: 12, color: 'var(--tx-mu)' }}>
              Sin bajas en este periodo.
            </div>
          ) : (
            <table className="hs-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Alumno / Deal</th>
                  <th style={{ textAlign: 'right' }}>Fecha venta</th>
                  <th style={{ textAlign: 'right' }}>Fecha baja</th>
                </tr>
              </thead>
              <tbody>
                {[...myRefunds]
                  .sort((a, b) => (b.closedate || '').localeCompare(a.closedate || ''))
                  .map((r, i) => (
                    <tr key={r.id || i}>
                      <td style={{ fontSize: 12 }}>{r.dealName || r.contactName || '—'}</td>
                      <td style={{ fontSize: 11, textAlign: 'right', color: 'var(--tx-mu)' }}>
                        {r.closedate ? r.closedate.slice(0, 10) : '—'}
                      </td>
                      <td style={{ fontSize: 11, textAlign: 'right', color: 'var(--tx-mu)' }}>
                        {r.refundDate ? r.refundDate.slice(0, 10) : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* ── IMPAGOS ── */}
        {myPayments.length > 0 && (
          <Section title="Impagos" count={myPayments.length} defaultOpen={false}>
            <table className="hs-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Deal</th>
                  <th style={{ textAlign: 'center' }}>Nº cuota</th>
                  <th style={{ textAlign: 'right' }}>Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {myPayments.map((p, i) => {
                  const d = dealById[p.dealId];
                  return (
                    <tr key={p.id || i}>
                      <td style={{ fontSize: 12 }}>{d?.dealName || d?.contactName || p.dealId}</td>
                      <td style={{ fontSize: 11, textAlign: 'center', color: 'var(--tx-mu)' }}>
                        {p.installmentNumber || '—'}
                      </td>
                      <td style={{ fontSize: 11, textAlign: 'right', color: 'var(--tx-mu)' }}>
                        {p.dueDate ? p.dueDate.slice(0, 10) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>
        )}

        {/* ── VENTAS ── */}
        <Section title="Ventas" count={myDeals.length} defaultOpen={false}>
          {myDeals.length === 0 ? (
            <div style={{ padding: '18px 22px', fontSize: 12, color: 'var(--tx-mu)' }}>
              Sin ventas en este periodo.
            </div>
          ) : (
            <table className="hs-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Cliente / Deal</th>
                  <th style={{ textAlign: 'right' }}>Importe</th>
                  <th style={{ textAlign: 'center' }}>Cuotas</th>
                  <th style={{ textAlign: 'right' }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {[...myDeals]
                  .sort((a, b) => (b.closedate || '').localeCompare(a.closedate || ''))
                  .map((d, i) => (
                    <tr key={d.id || i}>
                      <td style={{ fontSize: 12 }}>{d.dealName || d.contactName || '—'}</td>
                      <td style={{ fontSize: 12, textAlign: 'right', fontWeight: 600 }}>
                        {fmt(d.amount || 0)}
                      </td>
                      <td style={{ fontSize: 11, textAlign: 'center', color: 'var(--tx-mu)' }}>
                        {d.cuotas || 1}x
                      </td>
                      <td style={{ fontSize: 11, textAlign: 'right', color: 'var(--tx-mu)' }}>
                        {d.closedate ? d.closedate.slice(0, 10) : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </div>
  );
}

export function DrillModal({
  drill,
  onClose,
  selMonth,
  selYear,
  isYear = false,
  cards,
  comerciales,
  activeComerciales,
  teams,
  billing,
  billingInfo,
  commission,
  commPct,
  refundsMonth,
  paymentsMonth2,
  paymentsMonth3,
  finalPayout,
  adjustments,
  monthEntries,
  allUsers,
  ownerById,
  dealById,
  setProfileUser,
  goTo,
  // Lote-121: botón "Diagnosticar baja" en el drill de refunds.
  isManager = false,
  onOpenDiagnose = null,
}) {
  const titles = {
    comerciales: 'Comerciales activos',
    billing: 'Facturación bruta',
    commPct: '% Comisión / Facturación',
    commission: 'Comisión bruta',
    refunds: 'Bajas aceptadas',
    unpaid2: 'Impagos firmes 2ª cuota',
    unpaid3: 'Impagos firmes 3ª cuota',
    netPay: 'Pago neto',
  };
  const periodLabel = isYear ? String(selYear) : MONTHS[selMonth - 1] + ' ' + selYear;
  // Helper: navegar al perfil de un user desde el modal.
  const openProfile = userObj => {
    if (!userObj || !setProfileUser || !goTo) return;
    setProfileUser(userObj);
    goTo('users');
    onClose();
  };

  const body = (() => {
    if (drill === 'comerciales') {
      // Listado por equipo: agrupar comerciales del scope por teamId.
      const byTeam = {};
      comerciales.forEach(u => {
        const tid = u.teamId || '_sin_';
        if (!byTeam[tid]) byTeam[tid] = [];
        byTeam[tid].push(u);
      });
      const teamName = tid =>
        tid === '_sin_' ? 'Sin equipo' : (teams || []).find(t => t.id === tid)?.name || '—';
      // ¿Tiene actividad este mes?
      const activeIds = new Set(cards.filter(c => c.userId).map(c => c.userId));
      return (
        <div style={{ padding: '14px 22px' }}>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}
          >
            <div style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                Total scope
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>
                {comerciales.length}
              </div>
            </div>
            <div style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                Con comisión &gt; 0
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2, color: 'var(--pu)' }}>
                {activeComerciales}
              </div>
            </div>
          </div>
          {Object.entries(byTeam)
            .sort(([a], [b]) => teamName(a).localeCompare(teamName(b)))
            .map(([tid, users]) => (
              <div key={tid} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--tx-mu)',
                    textTransform: 'uppercase',
                    letterSpacing: '.4px',
                    marginBottom: 6,
                  }}
                >
                  {teamName(tid)} · {users.length}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {users
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(u => {
                      const active = activeIds.has(u.id);
                      return (
                        <div
                          key={u.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 8px',
                            background: 'var(--wh)',
                            borderRadius: 6,
                            border: '1px solid var(--bds)',
                          }}
                        >
                          <div className="uav com" style={{ width: 22, height: 22, fontSize: 10 }}>
                            {u.name[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 12, flex: 1 }}>{u.name}</span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: active ? 'var(--pu)' : 'var(--tx-mu)',
                            }}
                          >
                            {active ? '• activo' : 'sin ventas'}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
        </div>
      );
    }
    if (
      drill === 'billing' ||
      drill === 'commPct' ||
      drill === 'netPay' ||
      drill === 'commission'
    ) {
      // Desglose por comercial (cards aggregadas) — sort por la métrica relevante.
      const sortKey =
        drill === 'billing'
          ? 'billing'
          : drill === 'netPay'
            ? 'net'
            : drill === 'commission'
              ? 'gross'
              : 'billing';
      const rows = [...cards].sort((a, b) => b[sortKey] - a[sortKey]);
      const totLabel =
        drill === 'billing'
          ? 'Facturación total'
          : drill === 'netPay'
            ? 'Pago neto total'
            : drill === 'commission'
              ? 'Comisión bruta total'
              : '% Comisión / Facturación';
      const totVal =
        drill === 'billing'
          ? fmt(billing)
          : drill === 'netPay'
            ? fmt(finalPayout)
            : drill === 'commission'
              ? fmt(commission)
              : billing > 0
                ? commPct.toFixed(1) + '%'
                : '—';
      return (
        <div style={{ padding: '14px 22px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 10,
              marginBottom: 14,
            }}
          >
            <div style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                {totLabel}
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{totVal}</div>
            </div>
            <div style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                Comisión bruta
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{fmt(commission)}</div>
            </div>
            <div style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                Comerciales
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{cards.length}</div>
            </div>
          </div>
          <table className="hs-tbl" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Comercial</th>
                <th style={{ textAlign: 'right' }}>Facturación</th>
                <th style={{ textAlign: 'right' }}>Com. bruta</th>
                <th style={{ textAlign: 'right' }}>%</th>
                <th style={{ textAlign: 'right' }}>Pago neto</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const pctR = r.billing > 0 ? (r.gross / r.billing) * 100 : 0;
                const userObj = r.userId ? (allUsers || []).find(u => u.id === r.userId) : null;
                const clickable = !!(userObj && setProfileUser && goTo);
                return (
                  <tr
                    key={r.key}
                    style={{ cursor: clickable ? 'pointer' : 'default' }}
                    onClick={() => clickable && openProfile(userObj)}
                  >
                    <td>{r.name}</td>
                    <td style={{ textAlign: 'right', color: 'var(--tx-mu)' }}>{fmt(r.billing)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--tx-mu)' }}>{fmt(r.gross)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--tx-mu)' }}>
                      {r.billing > 0 ? pctR.toFixed(1) + '%' : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.net)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    if (drill === 'refunds') {
      const rows = [...refundsMonth].sort((a, b) =>
        (b.closedate || '').localeCompare(a.closedate || '')
      );
      return (
        <div style={{ padding: '14px 22px' }}>
          <div
            style={{
              padding: '8px 10px',
              background: 'var(--bg)',
              borderRadius: 8,
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                Bajas aceptadas en {periodLabel}
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2, color: 'var(--er)' }}>
                {refundsMonth.length}
              </div>
            </div>
            {isManager && onOpenDiagnose && (
              <button
                className="btn-secondary"
                style={{ fontSize: 11, padding: '6px 10px' }}
                onClick={onOpenDiagnose}
                title="Investigar por qué una baja específica aparece o no en esta vista"
              >
                🔍 Diagnosticar baja
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--tx-mu)' }}>
              Sin bajas en {isYear ? 'este año' : 'este mes'}.
            </p>
          ) : (
            <table className="hs-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Comercial</th>
                  <th>Contacto</th>
                  <th style={{ textAlign: 'right' }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.ownerName || '—'}</td>
                    <td style={{ color: 'var(--tx-mu)' }}>{r.contactName || r.dealName || '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--tx-mu)' }}>
                      {r.closedate ? r.closedate.slice(0, 10) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (drill === 'unpaid2' || drill === 'unpaid3') {
      const list = drill === 'unpaid2' ? paymentsMonth2 : paymentsMonth3;
      const num = drill === 'unpaid2' ? 2 : 3;
      return (
        <div style={{ padding: '14px 22px' }}>
          <div
            style={{
              padding: '8px 10px',
              background: 'var(--bg)',
              borderRadius: 8,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--tx-mu)',
                textTransform: 'uppercase',
                letterSpacing: '.4px',
              }}
            >
              Impagos firmes {num}ª cuota
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2, color: 'var(--or)' }}>
              {list.length}
            </div>
          </div>
          {list.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--tx-mu)' }}>
              Sin impagos de {num}ª cuota en {isYear ? 'este año' : 'este mes'}.
            </p>
          ) : (
            <table className="hs-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Comercial</th>
                  <th>Deal</th>
                  <th style={{ textAlign: 'right' }}>Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p, i) => {
                  const d = dealById[p.dealId];
                  const owner = ownerById[d?.ownerId];
                  return (
                    <tr key={i}>
                      <td>{owner?.name || d?.ownerName || '—'}</td>
                      <td style={{ color: 'var(--tx-mu)' }}>
                        {d?.dealName || d?.contactName || p.dealId}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--tx-mu)' }}>
                        {p.dueDate ? p.dueDate.slice(0, 10) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    return null;
  })();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,20,20,.35)',
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(680px,96vw)',
          height: '100vh',
          background: 'var(--wh)',
          overflow: 'auto',
          boxShadow: '-8px 0 24px rgba(0,0,0,.15)',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--bd)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            background: 'var(--wh)',
            zIndex: 1,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--tx-mu)',
                textTransform: 'uppercase',
                letterSpacing: '.4px',
              }}
            >
              Detalle
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>
              {titles[drill]} · {periodLabel}
            </div>
          </div>
          <button className="btn btn-gh btn-ic" onClick={onClose}>
            {Ic.close}
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}

// ── Calendario de eventos del equipo ──
// Card desplegable que lista próximos eventos. Manager puede crear/editar/borrar.
// Eventos típicos: cierre de mes, día de pago, freezes, reuniones, etc.
const EVENT_TYPES = {
  closure: { label: 'Cierre de mes', icon: '📅', color: '#8B5CF6' },
  pay: { label: 'Día de pago', icon: '💸', color: '#10B981' },
  meeting: { label: 'Reunión', icon: '🗓️', color: '#3B82F6' },
  freeze: { label: 'Merge freeze', icon: '🧊', color: '#F59E0B' },
  other: { label: 'Otro', icon: '📌', color: '#646462' },
};
function EventsCalendar({ isManager, teams = [], viewerTeamId = null }) {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: '',
    endDate: '',
    title: '',
    type: 'other',
    notes: '',
    teamIds: [],
  });
  const [editingId, setEditingId] = useState(null);

  const load = () =>
    fetch('/api/events')
      .then(r => r.json())
      .then(d => {
        setEvents(Array.isArray(d) ? d : []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  useEffect(() => {
    load();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  // Filtro de visibilidad por equipo:
  // - manager (sin viewerTeamId) ve todo.
  // - viewer con un equipo concreto: ve eventos con teamIds vacío (globales)
  //   o que incluyan su equipo.
  const visibleByTeam = e => {
    if (!viewerTeamId) return true;
    const ids = Array.isArray(e.teamIds) ? e.teamIds : [];
    return ids.length === 0 || ids.includes(viewerTeamId);
  };
  // Próximos: events con date >= hoy o (endDate >= hoy si tiene). Ordenados asc.
  const upcoming = events
    .filter(e => (e.endDate || e.date) >= today)
    .filter(visibleByTeam)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 12);

  const fmtDateEs = iso => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return `${d} ${MONTHS[m - 1]}`;
  };

  const teamNameById = id => teams.find(t => t.id === id)?.name || id;
  const toggleTeamInForm = id =>
    setForm(f => {
      const has = (f.teamIds || []).includes(id);
      return { ...f, teamIds: has ? f.teamIds.filter(x => x !== id) : [...(f.teamIds || []), id] };
    });

  const startCreate = () => {
    setForm({ date: today, endDate: '', title: '', type: 'other', notes: '', teamIds: [] });
    setEditingId(null);
    setShowForm(true);
  };
  const startEdit = e => {
    setForm({
      date: e.date,
      endDate: e.endDate || '',
      title: e.title,
      type: e.type || 'other',
      notes: e.notes || '',
      teamIds: Array.isArray(e.teamIds) ? e.teamIds : [],
    });
    setEditingId(e.id);
    setShowForm(true);
  };
  const save = async () => {
    if (!form.date || !form.title.trim()) return alert('Fecha y título son obligatorios');
    const body = {
      ...form,
      title: form.title.trim(),
      endDate: form.endDate || null,
      teamIds: form.teamIds || [],
    };
    const url = editingId ? `/api/events/${editingId}` : '/api/events';
    const method = editingId ? 'PUT' : 'POST';
    const r = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) return alert('Error al guardar');
    setShowForm(false);
    setEditingId(null);
    load();
  };
  const remove = async id => {
    if (!confirm('¿Eliminar este evento?')) return;
    const r = await fetch('/api/events/' + id, { method: 'DELETE' });
    if (r.ok) load();
  };

  if (!loaded) return null;
  if (!isManager && upcoming.length === 0) return null; // comerciales: no mostrar si vacío

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 18 }}>
      <div
        className="ch"
        style={{
          borderBottom: '1px solid var(--bds)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0 }}>🗓️ Calendario · próximos eventos</h3>
          <span className="pill pill-pu">{upcoming.length}</span>
        </div>
        {isManager && (
          <button className="btn btn-gh btn-sm" onClick={startCreate}>
            + Nuevo
          </button>
        )}
      </div>

      {showForm && (
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--bds)',
            background: 'var(--bg)',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: 8,
          }}
        >
          <input
            type="date"
            className="fi"
            value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
          />
          <input
            type="date"
            className="fi"
            value={form.endDate}
            onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
            placeholder="Hasta (opcional)"
          />
          <select
            className="fs"
            value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
          >
            {Object.entries(EVENT_TYPES).map(([k, v]) => (
              <option key={k} value={k}>
                {v.icon} {v.label}
              </option>
            ))}
          </select>
          <input
            className="fi"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Título"
          />
          <input
            className="fi"
            style={{ gridColumn: '1 / -1' }}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Notas (opcional)"
          />
          {teams.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.5px',
                  marginBottom: 6,
                }}
              >
                Equipos afectados{' '}
                <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--tx3)' }}>
                  · vacío = todos
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {teams.map(t => {
                  const on = (form.teamIds || []).includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTeamInForm(t.id)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 'var(--r)',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: '1px solid ' + (on ? 'var(--pu)' : 'var(--bds)'),
                        background: on ? 'var(--pu)' : 'transparent',
                        color: on ? '#fff' : 'var(--tx)',
                      }}
                    >
                      {on ? '✓ ' : ''}
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div
            style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}
          >
            <button
              className="btn btn-gh btn-sm"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
            >
              Cancelar
            </button>
            <button className="btn btn-pu btn-sm" onClick={save}>
              {editingId ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      {upcoming.length === 0 ? (
        <div
          style={{ padding: '22px 18px', fontSize: 12, color: 'var(--tx-mu)', textAlign: 'center' }}
        >
          Sin eventos próximos. {isManager && 'Pulsa + Nuevo para añadir uno.'}
        </div>
      ) : (
        <div>
          {upcoming.map(e => {
            const meta = EVENT_TYPES[e.type] || EVENT_TYPES.other;
            const dateRange =
              e.endDate && e.endDate !== e.date
                ? `${fmtDateEs(e.date)} – ${fmtDateEs(e.endDate)}`
                : fmtDateEs(e.date);
            const daysAway = Math.round((new Date(e.date) - new Date(today)) / 86400000);
            const dayLabel =
              daysAway === 0
                ? 'Hoy'
                : daysAway === 1
                  ? 'Mañana'
                  : daysAway > 0
                    ? `En ${daysAway} días`
                    : '';
            return (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 18px',
                  borderBottom: '1px solid var(--bds)',
                }}
              >
                <div
                  style={{
                    flex: '0 0 36px',
                    height: 36,
                    borderRadius: 8,
                    background: meta.color + '1a',
                    color: meta.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                  }}
                >
                  {meta.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: 'var(--tx)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>{e.title}</span>
                    {Array.isArray(e.teamIds) && e.teamIds.length > 0 && (
                      <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                        {e.teamIds.map(tid => (
                          <span
                            key={tid}
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '2px 6px',
                              borderRadius: 'var(--r)',
                              background: 'var(--bl)15',
                              color: 'var(--bl)',
                              border: '1px solid var(--bl)40',
                            }}
                          >
                            {teamNameById(tid)}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx-mu)', display: 'flex', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: meta.color }}>{meta.label}</span>
                    <span>· {dateRange}</span>
                    {dayLabel && <span>· {dayLabel}</span>}
                    {e.notes && (
                      <span
                        style={{
                          color: 'var(--tx3)',
                          maxWidth: 280,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        · {e.notes}
                      </span>
                    )}
                  </div>
                </div>
                {isManager && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-gh btn-sm" onClick={() => startEdit(e)}>
                      Editar
                    </button>
                    <button
                      className="btn btn-gh btn-sm"
                      onClick={() => remove(e.id)}
                      title="Eliminar"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── TLGoalsCard ───
// Tarjeta de progreso de comisión TL para el quarter en curso.
// - Si el TL no tiene plan TL asignado → empty state minimal con CTA a Carrera.
// - Si lo tiene → KPIs: facturación equipo / objetivo Q, comerciales que han
//   alcanzado el threshold y comisión TL estimada.
// Endpoint: GET /api/calculate-tl?userId=X&year=Y&quarter=Q
function TLGoalsCard({ activeUser, goTo }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const now = new Date();
  const year = now.getFullYear();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  useEffect(() => {
    if (!activeUser?.id) return;
    fetch(`/api/calculate-tl?userId=${activeUser.id}&year=${year}&quarter=${quarter}`)
      .then(r => r.json())
      .then(d => {
        if (d?.error) setErr(d.error);
        else setData(d);
        setLoaded(true);
      })
      .catch(e => {
        setErr(e.message || 'error');
        setLoaded(true);
      });
  }, [activeUser?.id, year, quarter]);

  if (!loaded) return null;

  // Empty state cuando no hay plan TL asignado (caso "vacío si no").
  if (err || !data) {
    return (
      <div
        className="card"
        style={{
          padding: '16px 18px',
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: 'var(--bl)15',
            color: 'var(--bl)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          👑
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx)' }}>
            Tu plan de Team Leader
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 2 }}>
            Aún no tienes un plan TL asignado · pide al super admin que te asigne uno para ver tu
            comisión y objetivos del Q
          </div>
        </div>
        {goTo && (
          <button className="btn btn-gh btn-sm" onClick={() => goTo('carrera')}>
            Ver mi carrera
          </button>
        )}
      </div>
    );
  }

  // lote-193: nuevo modelo. La variable = cumplimiento de la VERTICAL × fact. del
  // EQUIPO del TL ese Q. data: { option, quarterVariable, annualAdjustment,
  // verticalAnnualTarget, teamMembersCount, breakdown:{ qCompliance, teamQBilling,
  // verticalQBilling, ... } }.
  const option = data.option === 'opt2' ? 'opt2' : 'opt1';
  const bd = data.breakdown || {};
  const qCompliance = bd.qCompliance || 0;
  const complianceRatio = Math.min(1, qCompliance / 100);
  const teamQBilling = bd.teamQBilling || 0;
  const verticalQBilling = bd.verticalQBilling || 0;
  const verticalAnnualTarget = data.verticalAnnualTarget || 0;
  const verticalQTarget = verticalAnnualTarget / 4;
  const quarterVariable = data.quarterVariable || 0;
  const annualAdjustment = data.annualAdjustment || 0;
  const total = data.teamMembersCount || 0;
  const teamShare = verticalQBilling > 0 ? (teamQBilling / verticalQBilling) * 100 : 0;

  return (
    <div className="card" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
      <div
        className="ch"
        style={{
          borderBottom: '1px solid var(--bds)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0 }}>
            👑 Tu plan TL · Q{quarter} {year}
          </h3>
          {data.hasOverride && (
            <span className="pill" title="Override trimestral aplicado">
              override
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: 'var(--tx-mu)' }}>
          {total} comercial{total !== 1 ? 'es' : ''} en el equipo
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 0,
        }}
      >
        <div style={{ padding: '14px 18px', borderRight: '1px solid var(--bds)' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--tx-mu)',
              textTransform: 'uppercase',
              letterSpacing: '.4px',
            }}
          >
            Cumplimiento de la vertical (Q)
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx)', marginTop: 4 }}>
            {Math.round(qCompliance)}%
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 3 }}>
            {verticalQTarget > 0
              ? `${fmt(verticalQBilling)} / ${fmt(verticalQTarget)} objetivo Q`
              : 'Fija el objetivo anual de la vertical en Tramos'}
          </div>
          <div
            style={{
              height: 5,
              background: 'var(--bds)',
              borderRadius: 999,
              marginTop: 6,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: Math.min(100, complianceRatio * 100) + '%',
                background:
                  qCompliance >= 100
                    ? 'var(--gr-d)'
                    : qCompliance >= 80
                      ? 'var(--pu)'
                      : 'var(--er)',
                transition: 'width .25s',
              }}
            />
          </div>
        </div>
        <div style={{ padding: '14px 18px', borderRight: '1px solid var(--bds)' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--tx-mu)',
              textTransform: 'uppercase',
              letterSpacing: '.4px',
            }}
          >
            Facturación de tu equipo (Q)
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx)', marginTop: 4 }}>
            {fmt(teamQBilling)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 3 }}>
            {teamShare > 0 ? `${teamShare.toFixed(1)}% del Q de la vertical` : '—'}
          </div>
        </div>
        <div style={{ padding: '14px 18px' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--tx-mu)',
              textTransform: 'uppercase',
              letterSpacing: '.4px',
            }}
          >
            Tu variable TL (Q) · {option === 'opt2' ? 'Opción 2' : 'Opción 1'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--pu)', marginTop: 4 }}>
            {fmt(quarterVariable)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 3 }}>
            {option === 'opt2'
              ? `+ ajuste anual estimado ${fmt(annualAdjustment)} al cierre`
              : `${(bd.rate || 0).toString().replace('.', ',')}% sobre la fact. del equipo`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TeamObjectivesView ───────────────────────────────────────────────────────
// Tab "Equipo vs Objetivo" del Inicio. Comparativa de progreso de objetivos
// para TL/RO. Layout en 3 bloques:
//   1. Tarjeta agregada de equipo: facturación total vs objetivo total
//      con barra de progreso + % cumplido + #comerciales en/fuera de objetivo
//   2. Mini-tarjetas por comercial: cada uno con su barra individual,
//      ordenadas por % de cumplimiento descendente (los más rezagados al final)
//   3. Estilo "diff" verde/rojo para que el TL identifique focos
export function TeamObjectivesView({
  users,
  history,
  teams,
  teamFilter,
  selYear,
  selMonth,
  viewMode,
  rangeLabel,
  billing,
}) {
  const isYear = viewMode === 'year';

  // Lista de comerciales del scope activo (ya filtrada por el padre).
  const teamUsers = (users || []).filter(
    u => u.role === 'comercial' && (u.status || 'activo') === 'activo'
  );

  // Para cada user calculamos: objetivo del periodo + facturación lograda.
  // El objetivo vive en user.monthlyObjectives[year_MM] como número.
  // La facturación viene del cálculo agregado del padre — la sacamos del
  // history para que coincida con lo que se ve en otras vistas.
  const rows = teamUsers.map(u => {
    const objMap = u.monthlyObjectives || {};
    let objetivo = 0;
    let facturado = 0;
    if (isYear) {
      // Año: suma de los 12 meses (objetivo) + suma de facturación anual.
      // Lote-105: las claves de monthlyObjectives se guardan SIN padding
      // ("2026_5", no "2026_05") — desde perfil.jsx FieldRow Objetivo.
      // Antes leíamos con padStart(2,'0') y nunca matcheaba → 0 siempre.
      for (let m = 1; m <= 12; m++) {
        const k = `${selYear}_${m}`;
        objetivo += parseFloat(objMap[k]) || 0;
        const ent = (history || []).filter(
          h => h.userId === u.id && h.year === selYear && h.month === m
        );
        for (const h of ent)
          facturado += Object.values(h.billing || {}).reduce((s, v) => s + (v || 0), 0);
      }
    } else {
      const k = `${selYear}_${selMonth}`;
      objetivo = parseFloat(objMap[k]) || 0;
      const ent = (history || []).filter(
        h => h.userId === u.id && h.year === selYear && h.month === selMonth
      );
      for (const h of ent)
        facturado += Object.values(h.billing || {}).reduce((s, v) => s + (v || 0), 0);
    }
    const pct = objetivo > 0 ? Math.min(200, Math.round((facturado / objetivo) * 100)) : null;
    return { user: u, objetivo, facturado, pct };
  });

  // Agregado de equipo
  const teamObj = rows.reduce((s, r) => s + r.objetivo, 0);
  const teamFact = rows.reduce((s, r) => s + r.facturado, 0);
  const teamPct = teamObj > 0 ? Math.round((teamFact / teamObj) * 100) : null;
  const enObjetivo = rows.filter(r => r.pct != null && r.pct >= 100).length;
  const conObjetivo = rows.filter(r => r.objetivo > 0).length;

  // Orden: con objetivo primero (por % asc para destacar focos), luego los
  // que no tienen objetivo asignado (no aportan a la vista comparativa).
  const sorted = [...rows].sort((a, b) => {
    if (a.objetivo === 0 && b.objetivo === 0) return b.facturado - a.facturado;
    if (a.objetivo === 0) return 1;
    if (b.objetivo === 0) return -1;
    return (a.pct || 0) - (b.pct || 0);
  });

  const Bar = ({ pct, height = 8, neutral = false }) => {
    const p = Math.max(0, Math.min(100, pct || 0));
    const color = neutral
      ? 'var(--pu)'
      : p >= 100
        ? 'var(--gr-d)'
        : p >= 75
          ? 'var(--pu)'
          : p >= 50
            ? 'var(--or)'
            : 'var(--er)';
    return (
      <div
        style={{
          width: '100%',
          height,
          background: 'var(--bds)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{ width: p + '%', height: '100%', background: color, transition: 'width .3s' }}
        />
      </div>
    );
  };

  if (teamUsers.length === 0) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--tx-mu)' }}>
          Sin comerciales en el equipo seleccionado.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Bloque 1 — Agregado de equipo */}
      <div className="card" style={{ padding: '18px 22px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--tx-mu)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
              }}
            >
              Equipo · {rangeLabel}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--tx)',
                marginTop: 4,
                letterSpacing: '-.5px',
              }}
            >
              {fmt(teamFact)}{' '}
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--tx-mu)' }}>
                de {teamObj > 0 ? fmt(teamObj) : '— sin objetivo agregado'}
              </span>
            </div>
          </div>
          {teamPct != null && (
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: '-1px',
                color: teamPct >= 100 ? 'var(--gr-d)' : teamPct >= 75 ? 'var(--pu)' : 'var(--er)',
              }}
            >
              {teamPct}%
            </div>
          )}
        </div>
        {teamObj > 0 && <Bar pct={teamPct} height={12} />}
        <div
          style={{
            marginTop: 14,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 10,
          }}
        >
          {[
            ['Comerciales', String(teamUsers.length)],
            ['Con objetivo', `${conObjetivo} / ${teamUsers.length}`],
            ['En objetivo', `${enObjetivo} / ${conObjetivo}`],
            ['Facturación', fmt(teamFact)],
          ].map(([l, v]) => (
            <div key={l} style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tx-mu)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                }}
              >
                {l}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx)', marginTop: 3 }}>
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bloque 2 — Por comercial */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="ch"
          style={{
            borderBottom: '1px solid var(--bds)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3 style={{ margin: 0 }}>Por comercial</h3>
          <span style={{ fontSize: 11, color: 'var(--tx-mu)' }}>
            ordenados de más rezagado a más cumplido
          </span>
        </div>
        {sorted.map((r, i) => (
          <div
            key={r.user.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(180px, 1.5fr) 1fr minmax(110px, .6fr) 50px',
              gap: 14,
              alignItems: 'center',
              padding: '10px 18px',
              borderBottom: i < sorted.length - 1 ? '1px solid var(--bds)' : 'none',
              opacity: r.objetivo === 0 ? 0.6 : 1,
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--tx)' }}>{r.user.name}</div>
              <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 1 }}>
                {(teams || []).find(t => t.id === r.user.teamId)?.name || '— sin equipo'}
              </div>
            </div>
            <div>
              {r.objetivo > 0 ? (
                <Bar pct={r.pct} />
              ) : (
                <div style={{ fontSize: 11, color: 'var(--tx-mu)', fontStyle: 'italic' }}>
                  Sin objetivo asignado
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--tx2)' }}>
              <div style={{ fontWeight: 700, color: 'var(--tx)' }}>{fmt(r.facturado)}</div>
              {r.objetivo > 0 && (
                <div style={{ fontSize: 10, color: 'var(--tx-mu)' }}>obj {fmt(r.objetivo)}</div>
              )}
            </div>
            <div
              style={{
                textAlign: 'right',
                fontSize: 14,
                fontWeight: 800,
                color:
                  r.pct == null
                    ? 'var(--tx-mu)'
                    : r.pct >= 100
                      ? 'var(--gr-d)'
                      : r.pct >= 75
                        ? 'var(--pu)'
                        : r.pct >= 50
                          ? 'var(--or)'
                          : 'var(--er)',
              }}
            >
              {r.pct != null ? r.pct + '%' : '—'}
            </div>
          </div>
        ))}
        {sorted.length === 0 && (
          <div style={{ padding: 22, fontSize: 12, color: 'var(--tx-mu)', textAlign: 'center' }}>
            Sin datos.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TeamObjectivesEditor (lote-133) ─────────────────────────────────────
// Tabla editable de objetivos CONSOLIDADOS por equipo y sub-equipo.
// - En modo "Mes": 1 columna (el mes activo).
// - En modo "Año": 12 columnas (matriz anual).
// - Auto-save al perder foco. Visual feedback por celda en guardado.
// - Filas plegables: cada equipo padre + sub-equipos debajo (indentados).
// - El objetivo del padre es independiente del de sus sub-equipos: si lo
//   pones a nivel padre, ese es el target; los sub-equipos pueden tener
//   suyos propios para tracking interno. El hero del dashboard usa el del
//   equipo padre seleccionado.
//
// Distinto de monthlyObjectives individuales (en user.monthlyObjectives):
// estos consolidados pueden incluir B2B y residuales (deals sin owner
// individual), por eso necesitan su propio espacio.
export function TeamObjectivesEditor({
  teams,
  subteams,
  teamObjectives,
  selYear,
  selMonth,
  isYear,
  teamFilter,
  onSaved,
}) {
  const monthsToShow = isYear ? Array.from({ length: 12 }, (_, i) => i + 1) : [selMonth];
  const visibleTeams = teamFilter ? teams.filter(t => t.id === teamFilter) : teams;
  const [savingKey, setSavingKey] = useState(null);

  const getObj = (teamId, subteamId, year, month) => {
    const found = teamObjectives.find(
      o =>
        o.teamId === teamId &&
        (o.subteamId || null) === (subteamId || null) &&
        o.year === year &&
        o.month === month
    );
    return found ? String(found.amount) : '';
  };

  const saveObj = async (teamId, subteamId, year, month, raw) => {
    const amount = parseFloat(raw) || 0;
    const key = `${teamId}_${subteamId || ''}_${year}_${month}`;
    setSavingKey(key);
    try {
      await fetch('/api/team-objectives', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId, subteamId: subteamId || null, year, month, amount }),
      });
      if (onSaved) onSaved();
    } catch (e) {
      console.warn('[team-obj/save]', e?.message || e);
    } finally {
      setSavingKey(null);
    }
  };

  const cellInput = (teamId, subteamId, year, month) => {
    const key = `${teamId}_${subteamId || ''}_${year}_${month}`;
    const initial = getObj(teamId, subteamId, year, month);
    return (
      <input
        key={key + '_' + initial}
        type="number"
        min="0"
        step="100"
        defaultValue={initial}
        placeholder="—"
        onBlur={e => {
          if (e.target.value !== initial) {
            saveObj(teamId, subteamId, year, month, e.target.value);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') e.target.blur();
        }}
        style={{
          width: '100%',
          fontSize: 12,
          padding: '4px 6px',
          textAlign: 'right',
          background: initial ? 'var(--pu-bgl, #f3eefc)' : 'var(--wh)',
          border: '1px solid ' + (initial ? 'var(--pu)' : 'var(--bds)'),
          borderRadius: 4,
          fontWeight: initial ? 600 : 400,
          color: savingKey === key ? 'var(--tx-mu)' : 'var(--tx)',
          boxSizing: 'border-box',
          fontFamily: 'inherit',
        }}
      />
    );
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'auto', marginBottom: 20 }}>
      <div className="ch" style={{ borderBottom: '1px solid var(--bds)' }}>
        <h3>🎯 Objetivos consolidados de equipo</h3>
        <span style={{ fontSize: 11, color: 'var(--tx-mu)' }}>
          Incluyen B2B y residuales · independientes de los objetivos individuales de comerciales
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--bg)' }}>
            <th
              style={{
                padding: '10px 14px',
                textAlign: 'left',
                borderBottom: '2px solid var(--bds)',
                fontWeight: 600,
                color: 'var(--tx-mu)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '.4px',
                minWidth: 200,
                position: 'sticky',
                left: 0,
                background: 'var(--bg)',
                zIndex: 2,
              }}
            >
              Equipo / Sub-equipo
            </th>
            {monthsToShow.map(m => (
              <th
                key={m}
                style={{
                  padding: '10px 8px',
                  textAlign: 'center',
                  borderBottom: '2px solid var(--bds)',
                  fontWeight: 600,
                  color: 'var(--tx-mu)',
                  fontSize: 11,
                  minWidth: 100,
                }}
              >
                {MONTHS[m - 1].slice(0, 3)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleTeams.map(t => {
            const tSubs = (subteams || []).filter(s => s.teamId === t.id);
            return (
              <React.Fragment key={t.id}>
                <tr style={{ borderTop: '1px solid var(--bds)' }}>
                  <td
                    style={{
                      padding: '8px 14px',
                      fontWeight: 600,
                      position: 'sticky',
                      left: 0,
                      background: 'var(--wh)',
                      zIndex: 1,
                    }}
                  >
                    {t.name}
                  </td>
                  {monthsToShow.map(m => (
                    <td key={m} style={{ padding: '4px 6px' }}>
                      {cellInput(t.id, null, selYear, m)}
                    </td>
                  ))}
                </tr>
                {tSubs.map(s => (
                  <tr key={s.id}>
                    <td
                      style={{
                        padding: '8px 14px 8px 32px',
                        color: 'var(--tx-mu)',
                        position: 'sticky',
                        left: 0,
                        background: 'var(--wh)',
                        zIndex: 1,
                      }}
                    >
                      ↳ {s.name}
                    </td>
                    {monthsToShow.map(m => (
                      <td key={m} style={{ padding: '4px 6px' }}>
                        {cellInput(t.id, s.id, selYear, m)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {visibleTeams.length === 0 && (
        <div style={{ padding: 22, fontSize: 12, color: 'var(--tx-mu)', textAlign: 'center' }}>
          Selecciona un equipo para ver/editar objetivos.
        </div>
      )}
    </div>
  );
}
